import React, { useEffect, useState } from 'react';
import './RiskScoreCardGrid.scss';
import GaugeComponent from 'react-gauge-component';
import CountUp from 'react-countup';
import { ProgressBar } from 'react-bootstrap';
import AppRiskAnalysis from '../../../components/AppRiskAnalysis/AppRiskAnalysis';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { getLanguage } from '../../../util/languages';
import { useAuthState } from '../../../services/auth';
import {
  Activity,
  AppWindow,
  ArrowDownWideNarrow,
  ArrowRight,
  ArrowUp,
  Bug,
  CalendarClock,
  ChartColumn,
  Check,
  ChevronDown,
  ChevronUp,
  CircleAlert,
  CircleCheck,
  ClipboardList,
  Clock,
  Download,
  FileText,
  Fingerprint,
  Globe,
  Info,
  KeyRound,
  LayoutDashboard,
  ListFilter,
  Lock,
  LucideIcon,
  MessageSquare,
  MessageSquareWarning,
  Minus,
  Network,
  Plus,
  Scale,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Smartphone,
  TrendingDown,
  TrendingUp,
  TriangleAlert,
  WandSparkles,
  X,
  Zap,
} from 'lucide-react';
import { chartTokens } from '../../../design-system/chartTokens';
import { fonts } from '../../../design-system/tokens';
import type { BDTheme } from '../../../design-system/types';
// Define types based on your API response

// Enhanced Type Definitions
interface RiskScore {
  current: number;
  overall: number;
  overallChange: number;
  previousScores: number[];
  cvePercentage: number;
  dnsPercentage: number;
  phoneSmsPercentage: number;
}

interface DNSRecord {
  domain: string;
  category: 'normal' | 'malware' | 'scam' | 'phishing' | 'suspicious';
  likelihood: number;
  isSuspicious: boolean;
  timestamp: string;
  requestCount: number;
  threatLevel: 'low' | 'medium' | 'high' | 'critical';
}

interface DNSData {
  totalCount: number;
  suspiciousCount: number;
  categories: {
    normal: number;
    malware: number;
    scam: number;
    phishing: number;
    suspicious: number;
  };
  records: DNSRecord[];
  normalToSuspiciousRatio: {
    normal: number;
    suspicious: number;
  };
}

interface PhoneSMSRecord {
  number: string;
  threatType: 'scam' | 'phishing' | 'spam' | 'normal' | 'fraud';
  flagged: boolean;
  description?: string;
  timestamp: string;
  callDuration?: number;
  smsContent?: string;
  riskScore: number;
}

interface PhoneSMSData {
  totalCount: number;
  flaggedCount: number;
  records: PhoneSMSRecord[];
  threatBreakdown: {
    scam: number;
    phishing: number;
    spam: number;
    fraud: number;
  };
}

// Add CVE Data Interface
interface CVEData {
  totalCount: number;
  criticalCount: number;
  highCount: number;
  mediumCount: number;
  lowCount: number;
  riskScore: number;
  severityDistribution: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
}

interface RiskDescription {
  hasIssues: boolean;
  pointsIncrease: number;
  causes: string[];
  suggestions: string[];
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  timestamp: string;
}

interface DeviceRiskScore {
  deviceName: string;
  deviceType: string;
  deviceId: string;
  scoreLevel: 'excellent' | 'good' | 'fair' | 'poor' | 'critical';
  riskScore: RiskScore;
  dnsData: DNSData;
  phoneSMSData: PhoneSMSData;
  cveData: CVEData;
  description: RiskDescription;
  lastUpdated: string;
}

// Add interface for remediation action
interface RemediationAction {
  id: string;
  title: string;
  description: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  completed: boolean;
}

// ── Design-system colour scale ─────────────────────────────────────────────
// low = mint, medium = warn, high = danger, critical = crimson (--sev-critical,
// defined on .rsg in the SCSS). Values are CSS custom properties so inline styles
// follow the page's data-theme automatically.
type Tone = 'mint' | 'warn' | 'danger' | 'critical' | 'info' | 'violet' | 'neutral';

const toneVar: Record<Tone, string> = {
  mint: 'var(--accent)',
  warn: 'var(--warn)',
  danger: 'var(--danger)',
  critical: 'var(--sev-critical)',
  info: 'var(--info)',
  violet: 'var(--bd-violet)',
  neutral: 'var(--text-mute)',
};

const levelTone = (level: string): Tone => {
  switch (level) {
    case 'critical': return 'critical';
    case 'high': return 'danger';
    case 'medium': return 'warn';
    case 'low': return 'mint';
    default: return 'neutral';
  }
};

const threatTone = (threat: string): Tone => {
  switch (threat) {
    case 'normal': return 'mint';
    case 'scam': return 'danger';
    case 'phishing': return 'danger';
    case 'spam': return 'warn';
    case 'malware': return 'critical';
    case 'suspicious': return 'warn';
    default: return 'neutral';
  }
};

const scoreTone = (score: number): Tone => (score > 75 ? 'danger' : score > 25 ? 'warn' : 'mint');

/** Tinted chip in a tone of the scale (bd-chip base). */
const chip = (tone: Tone) => `bd-chip rsg-tone rsg-t-${tone}`;
/** Text colour utility for a tone. */
const toneText = (tone: Tone) => `rsg-c-${tone}`;

/** Follows data-theme on the Device Details page wrapper, for libraries that need concrete colours. */
const useDdTheme = (): BDTheme => {
  const read = (): BDTheme =>
    document.querySelector('.dd-page')?.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
  const [theme, setTheme] = useState<BDTheme>(read);
  useEffect(() => {
    setTheme(read());
    const el = document.querySelector('.dd-page');
    if (!el) return undefined;
    const observer = new MutationObserver(() => setTheme(read()));
    observer.observe(el, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);
  return theme;
};

const StatTile: React.FC<{ value: React.ReactNode; label: string; tone?: Tone }> = ({ value, label, tone }) => (
  <div className={`rsg-stat${tone ? ` rsg-t-${tone}` : ''}`}>
    <span className="bd-stat-num rsg-stat-num">{value}</span>
    <span className="rsg-stat-label">{label}</span>
  </div>
);

const Callout: React.FC<{ tone: Tone; icon: React.ReactNode; title: React.ReactNode; children?: React.ReactNode }> = ({
  tone, icon, title, children,
}) => (
  <div className={`rsg-callout rsg-callout-note rsg-t-${tone}`}>
    <span className="rsg-ico sm">{icon}</span>
    <div className="rsg-id-text">
      <span className="rsg-strong">{title}</span>
      {children && <span className="bd-body rsg-callout-text">{children}</span>}
    </div>
  </div>
);

// Enhanced component with additional sections
const RiskScoreCardGrid: React.FC<{ deviceData: DeviceRiskScore }> = ({ deviceData }) => {
  type TabType = 'overview' | 'dns' | 'logs' | 'phone' | 'health';
  const [activeTab, setActiveTab] = useState<TabType>('health');
  const [isAlertAcknowledged, setIsAlertAcknowledged] = useState(false);
  const [isAlertHidden, setIsAlertHidden] = useState(true);
  const { deviceId } = useParams();
    const { token } = useAuthState();
    const [devicesRiskReport, setDevicesRiskReport] = useState<any>();
  const theme = useDdTheme();
  const ct = chartTokens[theme];
  const { t, i18n } = useTranslation();
  const locale = getLanguage(i18n.language).locale;
  // Fixed label sets (risk levels, threat types) are translated at render; unknown server values fall back to the raw text
  const levelLabel = (level: string) => t(`riskScore.levels.${level}`, { defaultValue: level });
  const threatTypeLabel = (type: string) => t(`riskScore.threatTypes.${String(type).toLowerCase()}`, { defaultValue: type });
  const mono = { mono: <span className="bd-mono" /> };
  // State for slider
  const [currentSlide, setCurrentSlide] = useState(0);
  const [expandedSections, setExpandedSections] = useState<string[]>(['immediate']);

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };
  // State for recommendations
  const [healthScore, setHealthScore] = useState<number>(() => {
    // Calculate health score based on device data
    const baseScore = 100 - devicesRiskReport?.report?.scores?.final || 0;
    return baseScore > 0 ? baseScore : 0;
  });
  // Helper function to calculate overall progress
  const calculateOverallProgress = () => {
    const criticalCount = deviceData.cveData?.criticalCount || 0;
    const totalCount = deviceData.cveData?.totalCount || 0;
    const cvePercentage = deviceData.riskScore?.cvePercentage || 0;

    // Calculate progress based on available data
    if (criticalCount === 0) {
      return 75; // Good progress if no critical issues
    }

    // Adjust progress based on risk factors
    let progress = 0;
    if (criticalCount > 0) progress += 25; // Immediate section
    if (cvePercentage < 70) progress += 25; // Short-term section
    progress += 25; // Long-term section (always some progress)

    return Math.min(progress, 100);
  };
  const [remediationActions, setRemediationActions] = useState<RemediationAction[]>([
    {
      id: '1',
      title: 'Patch Critical Vulnerabilities',
      description: 'Update all apps with critical CVE vulnerabilities',
      priority: deviceData.cveData.criticalCount > 0 ? 'critical' : 'low',
      completed: false
    },
    {
      id: '2',
      title: 'Review App Permissions',
      description: 'Check and restrict unnecessary app permissions',
      priority: deviceData.riskScore.cvePercentage > 40 ? 'high' : 'medium',
      completed: false
    },
    {
      id: '3',
      title: 'Enable DNS Protection',
      description: 'Configure DNS filtering to block malicious domains',
      priority: deviceData.dnsData.suspiciousCount > 0 ? 'high' : 'low',
      completed: false
    },
    {
      id: '4',
      title: 'Setup Spam Protection',
      description: 'Enable enhanced spam filtering for calls and SMS',
      priority: deviceData.phoneSMSData.flaggedCount > 0 ? 'medium' : 'low',
      completed: false
    },
    {
      id: '5',
      title: 'Regular Security Audit',
      description: 'Schedule weekly security reviews and scans',
      priority: 'low',
      completed: false
    }
  ]);

  // Navigation functions
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === 2 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? 2 : prev - 1));
  };

  // Auto-advance slider (optional)
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 8000); // Change slide every 8 seconds

    return () => clearInterval(interval);
  }, []);


  // Add this function to handle acknowledgment
  const handleAcknowledgeAlert = () => {
    setIsAlertAcknowledged(true);
    console.log('Security alert acknowledged at:', new Date().toISOString());
  };

  // Optional: Reset acknowledgment when device data changes
  useEffect(() => {
    setIsAlertAcknowledged(false);
  }, [deviceData]);

  // Handle fixing all recommendations
  const handleFixAll = () => {
    console.log('Applying all security recommendations...');
    // In a real app, this would trigger actual security actions
    setRemediationActions(prev => prev.map(action => ({
      ...action,
      completed: action.priority !== 'critical' // Don't auto-complete critical actions
    })));

    // Update health score
    setHealthScore(prev => {
      const newScore = Math.min(prev + 20, 100);
      return newScore;
    });

    // Show success message
    alert(t('riskScore.notifications.fixAllApplied'));
  };

  const getScoreColor = (level: string) => {
    switch (level) {
      case 'excellent': return toneVar.mint;
      case 'good': return toneVar.mint;
      case 'fair': return toneVar.warn;
      case 'poor': return toneVar.danger;
      case 'critical': return toneVar.critical;
      default: return toneVar.neutral;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'low': return toneVar.mint;
      case 'medium': return toneVar.warn;
      case 'high': return toneVar.danger;
      case 'critical': return toneVar.critical;
      default: return toneVar.neutral;
    }
  };
interface NotificationType {
  show: boolean;
  type: 'success' | 'info' | 'warning' | 'error';
  message: string;
  title: string;
  progress?: number; // Optional progress percentage
  action?: {
    label: string;
    onClick: () => void;
  };
}

// Initial state
const [notification, setNotification] = useState<NotificationType>({
  show: false,
  type: 'success',
  message: '',
  title: '',
  progress: 100, // Starts at 100%, counts down to 0%
});

// Enhanced handleQuickFix with navigation
const handleQuickFix = (type: 'critical' | 'network' | 'communication' | 'app' | 'auto' | 'all') => {
  let notificationTitle = '';
  let notificationMessage = '';
  let notificationType: 'success' | 'info' | 'warning' | 'error' = 'info';
  
  // Navigate to appropriate tab based on type
  if (type === 'app') {
    setActiveTab('logs'); // Navigate to App Risk Analysis tab
  } else if (type === 'network') {
    setActiveTab('dns'); // Navigate to DNS Analysis tab
  } else if (type === 'communication') {
    setActiveTab('phone'); // Navigate to Phone/SMS tab
  }
  
  switch(type) {
    case 'critical':
      notificationTitle = t('riskScore.notifications.critical.title');
      notificationMessage = t('riskScore.notifications.critical.message');
      notificationType = 'warning';
      break;
    case 'network':
      notificationTitle = t('riskScore.notifications.network.title');
      notificationMessage = t('riskScore.notifications.network.message');
      notificationType = 'info';
      break;
    case 'communication':
      notificationTitle = t('riskScore.notifications.communication.title');
      notificationMessage = t('riskScore.notifications.communication.message');
      notificationType = 'info';
      break;
    case 'app':
      notificationTitle = t('riskScore.notifications.app.title');
      notificationMessage = t('riskScore.notifications.app.message');
      notificationType = 'warning';
      break;
    case 'auto':
      notificationTitle = t('riskScore.notifications.auto.title');
      notificationMessage = t('riskScore.notifications.auto.message');
      notificationType = 'success';
      break;
    case 'all':
      notificationTitle = t('riskScore.notifications.all.title');
      notificationMessage = t('riskScore.notifications.all.message');
      notificationType = 'success';
      break;
  }
  
  setNotification({
    show: false, // Reset before showing new notification
    type: notificationType,
    message: notificationMessage,
    title: notificationTitle,
    progress: 100,
    action: {
      label: t('riskScore.common.viewDetails'),
      onClick: () => {
        // Scroll to relevant section
        const securityPlan = document.querySelector('.recommendations-grid');
        if (securityPlan) {
          securityPlan.scrollIntoView({ behavior: 'smooth' });
        }
        setNotification(prev => ({ ...prev, show: false }));
      }
    }
  });
  
  // Simulate progress
  let progress = 100;
  const interval = setInterval(() => {
    progress -= 1;
    setNotification(prev => ({ ...prev, progress }));
    
    if (progress <= 0) {
      clearInterval(interval);
      setTimeout(() => {
        setNotification(prev => ({ ...prev, show: false }));
      }, 1000);
    }
  }, 30); // 3 seconds total
  
  // Auto-hide after 4 seconds
  setTimeout(() => {
    clearInterval(interval);
    setNotification(prev => ({ ...prev, show: false }));
  }, 4000);
};
  useEffect(() => {
    if (token) {
      fetchDeviceRiskReport();
    }

  }, [token]);

  async function fetchDeviceRiskReport() {
    try {
      const response = await axios.get(`/v2/op/device/${deviceId}/device-risk-report`);
      setDevicesRiskReport(response.data.response[0]);
    } catch (error) {
      console.error('Error fetching device risk:', error);
    }
  }
  const getThreatColor = (threat: string) => toneVar[threatTone(threat)];

  const getDynamicColors = (value: number) => {
    return [
      value < -33.33 ? ct.danger : ct.track, // Left arc
      value >= -33.33 && value <= 33.33 ? ct.accent : ct.track, // Middle arc
      value > 33.33 ? ct.danger : ct.track // Right arc
    ];
  };


  const getDynamicColorsHealth = (value: number) => {

    return [
      value < 33.33 ? ct.danger : ct.track, // Left arc
      value >= 33.33 && value <= 66.66 ? ct.warn : ct.track, // Middle arc
      value > 66.66 ? ct.accent : ct.track // Right arc
    ];
  };

  // ── Presentation-only derived values (tones for the design-system colour scale) ──
  const totalIssues = deviceData.cveData.totalCount + deviceData.dnsData.suspiciousCount + deviceData.phoneSMSData.flaggedCount;
  const alertTone = levelTone(deviceData.description.riskLevel);
  const alertLevel = ['critical', 'high', 'medium'].includes(deviceData.description.riskLevel) ? deviceData.description.riskLevel : 'low';
  const notifTone: Tone = notification.type === 'success' ? 'mint' :
    notification.type === 'info' ? 'info' :
    notification.type === 'warning' ? 'warn' : 'danger';
  const dnsTone: Tone = deviceData.dnsData.suspiciousCount === 0 ? 'mint' :
    deviceData.dnsData.suspiciousCount < 10 ? 'warn' : 'danger';
  const phoneTone: Tone = deviceData.phoneSMSData.flaggedCount === 0 ? 'mint' :
    deviceData.phoneSMSData.flaggedCount < 3 ? 'warn' : 'danger';
  const secTone: Tone = deviceData.riskScore.cvePercentage > 70 || deviceData.cveData.criticalCount > 0 ? 'critical' :
    deviceData.riskScore.cvePercentage > 40 || deviceData.cveData.highCount > 0 ? 'danger' : 'mint';
  const secThreatTone: Tone = deviceData.riskScore.cvePercentage > 70 || deviceData.cveData.criticalCount > 0 ? 'critical' :
    deviceData.riskScore.cvePercentage > 40 || deviceData.cveData.highCount > 0 ? 'danger' :
      deviceData.cveData.mediumCount > 0 ? 'warn' : 'mint';
  const permTone: Tone = deviceData.riskScore.cvePercentage > 70 ? 'danger' :
    deviceData.riskScore.cvePercentage > 40 ? 'warn' : 'mint';
  const healthTone: Tone = healthScore >= 80 ? 'mint' : healthScore >= 60 ? 'warn' : healthScore >= 40 ? 'warn' : 'danger';
  const overallHealth = 100 - devicesRiskReport?.report?.scores?.overall;
  const overallTone: Tone = overallHealth >= 60 ? 'mint' : overallHealth >= 40 ? 'warn' : 'danger';
  const riskChange = deviceData.riskScore.overallChange;
  const subTabs: { id: TabType; label: string; Icon: LucideIcon }[] = [
    { id: 'health', label: t('riskScore.tabs.health'), Icon: ShieldCheck },
    { id: 'overview', label: t('riskScore.tabs.overview'), Icon: LayoutDashboard },
    { id: 'dns', label: t('riskScore.tabs.dns'), Icon: Globe },
    { id: 'phone', label: t('riskScore.tabs.phone'), Icon: MessageSquareWarning },
    { id: 'logs', label: t('riskScore.tabs.logs'), Icon: AppWindow },
  ];
  const fmtShort = (value: string) => new Date(value).toLocaleString(locale, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className="rsg">
      {/* Section header */}
      <header className="rsg-hd">
        <h2 className="bd-h2">{t('riskScore.title')}</h2>
        <p className="bd-body">{t('riskScore.subtitle')}</p>
      </header>

      {/* Toast notification (fixed position) */}
      {notification.show && (
        <div className={`rsg-toast rsg-t-${notifTone}`} role="status">
          <div className="rsg-toast-bar">
            <i style={{ animation: 'progressBar 3s linear forwards', transformOrigin: 'left' }} />
          </div>
          <div className="rsg-toast-body">
            <span className="rsg-ico">
              {notification.type === 'success' && <CircleCheck size={18} strokeWidth={1.9} />}
              {notification.type === 'info' && <Info size={18} strokeWidth={1.9} />}
              {notification.type === 'warning' && <TriangleAlert size={18} strokeWidth={1.9} />}
              {notification.type === 'error' && <CircleAlert size={18} strokeWidth={1.9} />}
            </span>
            <div className="rsg-toast-text">
              <div className="rsg-toast-title">
                <h3 className="bd-h3">{notification.title}</h3>
                <span className={chip(notifTone)}>
                  {notification.type === 'success' ? t('riskScore.toast.success') :
                    notification.type === 'info' ? t('riskScore.toast.info') :
                      notification.type === 'warning' ? t('riskScore.toast.warning') : t('riskScore.toast.error')}
                </span>
              </div>
              <p className="bd-body">{notification.message}</p>
              <div className="rsg-actions">
                <button
                  type="button"
                  className="bd-btn primary rsg-btn-sm"
                  onClick={() => {
                    // Add action based on notification type
                    console.log('Action clicked for:', notification.title);
                    setNotification(prev => ({ ...prev, show: false }));
                  }}
                >
                  {t('riskScore.common.viewDetails')}
                </button>
                <button
                  type="button"
                  className="bd-btn ghost rsg-btn-sm"
                  onClick={() => setNotification(prev => ({ ...prev, show: false }))}
                >
                  {t('riskScore.common.dismiss')}
                </button>
              </div>
              <div className="rsg-toast-time bd-meta">
                <Clock size={13} strokeWidth={1.9} />
                <span>{t('riskScore.common.justNow')}</span>
                <div className="bd-progress rsg-toast-progress">
                  <i style={{ animation: 'progressBar 3s linear forwards', transformOrigin: 'left' }} />
                </div>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="bd-iconbtn rsg-iconbtn rsg-toast-close"
            onClick={() => setNotification(prev => ({ ...prev, show: false }))}
            aria-label={t('riskScore.common.closeNotification')}
          >
            <X size={14} strokeWidth={2} />
          </button>
        </div>
      )}

      {/* Security alert (collapsible) */}
      {deviceData.description.hasIssues && (
        <div
          className={`rsg-alert rsg-t-${alertTone}${isAlertHidden ? ' is-collapsed' : ''}`}
          onClick={() => isAlertHidden && setIsAlertHidden(false)}
        >
          {/* Minimized View when Hidden */}
          {isAlertHidden && (
            <div className="rsg-alert-min">
              <div className="rsg-card-id">
                <span className="rsg-ico rsg-pulse"><TriangleAlert size={18} strokeWidth={1.9} /></span>
                <div className="rsg-id-text">
                  <h3 className="bd-h3">{t('riskScore.alert.title')}</h3>
                  <div className="rsg-inline">
                    <span className={chip(alertTone)}>
                      <span className="dot" />
                      {levelLabel(deviceData.description.riskLevel).toLocaleUpperCase(locale)}
                    </span>
                    <span className="bd-meta bd-mono">{t('riskScore.alert.riskPoints', { count: deviceData.description.pointsIncrease })}</span>
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="bd-iconbtn rsg-iconbtn"
                aria-label={t('riskScore.alert.expand')}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsAlertHidden(false);
                }}
              >
                <Plus size={16} strokeWidth={2} />
              </button>
            </div>
          )}

          {/* Full View when Expanded */}
          {!isAlertHidden && (
            <>
              <div className="rsg-alert-hd">
                <div className="rsg-card-id">
                  <span className="rsg-ico lg"><TriangleAlert size={22} strokeWidth={1.9} /></span>
                  <div className="rsg-id-text">
                    <div className="rsg-inline">
                      <span className={chip('danger')}><span className="dot" />{t('riskScore.alert.chip')}</span>
                      <span className={chip(alertTone)}>
                        <TriangleAlert size={12} strokeWidth={2} />
                        {t('riskScore.alert.riskLevelChip', { level: levelLabel(deviceData.description.riskLevel).toLocaleUpperCase(locale) })}
                      </span>
                    </div>
                    <h3 className="bd-h2">{t('riskScore.alert.heading')}</h3>
                    <p className="bd-body">{t('riskScore.alert.headingSub')}</p>
                  </div>
                </div>
                <div className="rsg-alert-side">
                  <div className="rsg-nested rsg-alert-time">
                    <span className="bd-field-label">{t('riskScore.alert.generated')}</span>
                    <span className="bd-mono rsg-strong">
                      {deviceData.description.timestamp
                        ? new Date(deviceData.description.timestamp).toLocaleString(locale, {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                        : t('riskScore.common.na')}
                    </span>
                    <span className="bd-meta bd-mono">
                      {deviceData.description.timestamp
                        ? new Date(deviceData.description.timestamp).toLocaleDateString(locale, {
                          year: 'numeric'
                        })
                        : ''}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="bd-iconbtn rsg-iconbtn"
                    aria-label={t('riskScore.alert.hideDetailsAria')}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsAlertHidden(true);
                    }}
                  >
                    <ChevronUp size={16} strokeWidth={2} />
                  </button>
                </div>
              </div>

              <div className="rsg-alert-bd">
                {/* Risk Points Increase */}
                <div className="rsg-callout rsg-t-danger rsg-callout-row">
                  <div className="rsg-card-id">
                    <span className="rsg-ico"><Scale size={18} strokeWidth={1.9} /></span>
                    <div className="rsg-id-text">
                      <span className="rsg-strong">{t('riskScore.alert.riskAssessment')}</span>
                      <span className="bd-meta">{t('riskScore.alert.riskAssessmentSub')}</span>
                    </div>
                  </div>
                  <div className="rsg-end">
                    <span className="bd-stat-num rsg-tone-text">+{deviceData.description.pointsIncrease}</span>
                    <span className="bd-meta">{t('riskScore.alert.pointsIncrease')}</span>
                  </div>
                </div>

                {/* Causes & Recommendations */}
                <div className="rsg-grid-2">
                  <div className="rsg-nested rsg-t-danger">
                    <div className="rsg-card-id rsg-block-hd">
                      <span className="rsg-ico"><Zap size={18} strokeWidth={1.9} /></span>
                      <div className="rsg-id-text">
                        <h4 className="bd-h3">{t('riskScore.alert.rootCauses')}</h4>
                        <p className="bd-meta">{t('riskScore.alert.rootCausesSub')}</p>
                      </div>
                    </div>
                    <ol className="rsg-list">
                      {deviceData.description.causes.map((cause, index) => (
                        <li key={index} className="rsg-list-item">
                          <span className="rsg-num bd-mono">{index + 1}</span>
                          <span>{cause}</span>
                        </li>
                      ))}
                    </ol>
                  </div>

                  <div className="rsg-nested rsg-t-mint">
                    <div className="rsg-card-id rsg-block-hd">
                      <span className="rsg-ico"><CircleCheck size={18} strokeWidth={1.9} /></span>
                      <div className="rsg-id-text">
                        <h4 className="bd-h3">{t('riskScore.alert.remediationPlan')}</h4>
                        <p className="bd-meta">{t('riskScore.alert.remediationPlanSub')}</p>
                      </div>
                    </div>
                    <ul className="rsg-list">
                      {deviceData.description.suggestions.map((suggestion, index) => (
                        <li key={index} className="rsg-list-item">
                          <span className="rsg-num"><Check size={12} strokeWidth={2.4} /></span>
                          <span>{suggestion}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Priority Level & Action Section */}
                <div className="rsg-priority-row">
                  <div className={`rsg-callout rsg-callout-row rsg-t-${alertTone}`}>
                    <div className="rsg-card-id">
                      <span className="rsg-ico"><TriangleAlert size={20} strokeWidth={1.9} /></span>
                      <div className="rsg-id-text">
                        <span className="rsg-strong">{t('riskScore.alert.responsePriority')}</span>
                        <span className="bd-meta">
                          {t(`riskScore.alert.priorityText.${alertLevel}`)}
                        </span>
                      </div>
                    </div>
                    <div className="rsg-end">
                      <span className="bd-mono rsg-priority-word rsg-tone-text">
                        {t(`riskScore.alert.priorityWord.${alertLevel}`)}
                      </span>
                      <span className="bd-field-label rsg-flush">{t('riskScore.alert.priority')}</span>
                    </div>
                  </div>

                  <div className="rsg-nested rsg-priority-actions">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsAlertHidden(true);
                      }}
                      className="bd-btn danger-soft"
                    >
                      <ChevronUp size={16} strokeWidth={2} />
                      {t('riskScore.alert.hide')}
                    </button>
                    <button type="button" className="bd-btn secondary">
                      <Info size={16} strokeWidth={1.9} />
                      {t('riskScore.common.viewDetails')}
                    </button>
                  </div>
                </div>

                {/* Additional Context */}
                <Callout tone="info" icon={<Info size={18} strokeWidth={1.9} />} title={t('riskScore.alert.advisoryTitle')}>
                  {t('riskScore.alert.advisory')}
                </Callout>
              </div>
            </>
          )}
        </div>
      )}

      {/* Sub-view navigation */}
      <div className="bd-segmented rsg-tabs" role="tablist" aria-label={t('riskScore.tabs.ariaLabel')}>
        {subTabs.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={activeTab === id}
            className={activeTab === id ? 'active' : ''}
            onClick={() => setActiveTab(id)}
          >
            <Icon size={15} strokeWidth={1.9} />
            {label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="rsg-content">

        {/* Overview Tab Content */}
        {activeTab === 'overview' && (
          <div className="rsg-overview">
            {/* DNS Overview Card */}
            <section className="bd-card rsg-card">
              <div className="rsg-card-hd">
                <div className="rsg-card-id">
                  <span className="rsg-ico rsg-t-info"><Globe size={18} strokeWidth={1.9} /></span>
                  <div className="rsg-id-text">
                    <h3 className="bd-h3">{t('riskScore.overview.dnsTitle')}</h3>
                    <p className="bd-meta">{t('riskScore.overview.dnsSub')}</p>
                  </div>
                </div>
                <span className={chip(deviceData.dnsData.suspiciousCount > 0 ? 'danger' : 'mint')}>
                  <span className="dot" />
                  {deviceData.dnsData.suspiciousCount > 0 ? t('riskScore.common.threatsDetected') : t('riskScore.common.allClear')}
                </span>
              </div>

              <div className="rsg-stats">
                <StatTile value={deviceData.dnsData.totalCount.toLocaleString(locale)} label={t('riskScore.common.totalQueries')} />
                <StatTile value={deviceData.dnsData.suspiciousCount.toLocaleString(locale)} label={t('riskScore.common.suspicious')} tone="danger" />
                <StatTile
                  value={`${((deviceData.dnsData.categories.normal / deviceData.dnsData.totalCount) * 100).toFixed(1)}%`}
                  label={t('riskScore.common.safeRate')}
                  tone="mint"
                />
                <StatTile
                  value={`${((deviceData.dnsData.suspiciousCount / deviceData.dnsData.totalCount) * 100).toFixed(1)}%`}
                  label={t('riskScore.common.threatRatio')}
                  tone="warn"
                />
              </div>

              <div className="rsg-card-bd">
                {/* Category Distribution */}
                <div className="rsg-block">
                  <h4 className="rsg-block-title"><ListFilter size={16} strokeWidth={1.9} />{t('riskScore.overview.categoryDistribution')}</h4>
                  <div className="rsg-stack">
                    {devicesRiskReport?.report?.statistics?.dns?.map((dnsStat: { type: string; count: number; percentage: number }, index: number) => {
                      const category = dnsStat.type || 'uncategorized';
                      const isHighRisk = ['malware', 'phishing', 'scam'].includes(category.toLowerCase());

                      return (
                        <div key={index} className="rsg-nested rsg-dist">
                          <div className="rsg-split">
                            <span className="rsg-dist-name">
                              <span className="rsg-dot" style={{ background: getThreatColor(category) }} />
                              <span className={isHighRisk ? 'rsg-c-danger' : ''}>{threatTypeLabel(category)}</span>
                            </span>
                            <span className="bd-mono rsg-dist-val" style={{ color: getThreatColor(category) }}>
                              {dnsStat.percentage?.toFixed(1) || dnsStat.count}%
                            </span>
                          </div>
                          <div className="bd-progress">
                            <i style={{ width: `${dnsStat.percentage || 0}%`, background: getThreatColor(category) }} />
                          </div>
                          <div className="rsg-split bd-meta bd-mono">
                            <span>{t('riskScore.overview.queries', { count: dnsStat.count })}</span>
                            <span>{t('riskScore.overview.ofTotal', { value: dnsStat.percentage?.toFixed(1) })}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Risk Summary */}
                <div className="rsg-grid-2">
                  <div className="rsg-nested">
                    <h5 className="rsg-block-title sm"><ShieldCheck size={15} strokeWidth={1.9} />{t('riskScore.overview.trafficHealth')}</h5>
                    <div className="rsg-split">
                      <div className="rsg-id-text">
                        <span className="bd-stat-num rsg-num-md rsg-c-mint">{deviceData.dnsData.normalToSuspiciousRatio.normal}%</span>
                        <span className="bd-meta">{t('riskScore.common.normal')}</span>
                      </div>
                      <div className="rsg-end">
                        <span className="bd-stat-num rsg-num-md rsg-c-danger">{deviceData.dnsData.normalToSuspiciousRatio.suspicious}%</span>
                        <span className="bd-meta">{t('riskScore.common.suspicious')}</span>
                      </div>
                    </div>
                    <div className="bd-progress">
                      <i style={{ width: `${deviceData.dnsData.normalToSuspiciousRatio.normal}%` }} />
                    </div>
                  </div>

                  <div className={`rsg-nested rsg-level-card rsg-t-${dnsTone}`}>
                    <h5 className="rsg-block-title sm"><TriangleAlert size={15} strokeWidth={1.9} />{t('riskScore.common.threatAssessment')}</h5>
                    <span className="rsg-level rsg-tone-text">
                      {deviceData.dnsData.suspiciousCount === 0 ? t('riskScore.riskLabels.low') :
                        deviceData.dnsData.suspiciousCount < 10 ? t('riskScore.riskLabels.medium') : t('riskScore.riskLabels.high')}
                    </span>
                    <span className="bd-meta">{t('riskScore.overview.suspiciousActivities', { count: deviceData.dnsData.suspiciousCount })}</span>
                    <span className={chip(dnsTone)}>
                      <span className="dot" />
                      {deviceData.dnsData.suspiciousCount === 0 ? t('riskScore.common.protected') :
                        deviceData.dnsData.suspiciousCount < 10 ? t('riskScore.common.monitor') : t('riskScore.common.actionRequired')}
                    </span>
                  </div>
                </div>

                {/* Additional Insights */}
                <Callout tone="info" icon={<Info size={17} strokeWidth={1.9} />} title={t('riskScore.common.securityInsight')}>
                  {deviceData.dnsData.suspiciousCount > 0
                    ? t('riskScore.overview.dnsInsightThreats', { count: deviceData.dnsData.suspiciousCount })
                    : t('riskScore.overview.dnsInsightClear')
                  }
                </Callout>
              </div>
            </section>

            {/* Phone/SMS Overview Card */}
            <section className="bd-card rsg-card">
              <div className="rsg-card-hd">
                <div className="rsg-card-id">
                  <span className="rsg-ico rsg-t-violet"><MessageSquareWarning size={18} strokeWidth={1.9} /></span>
                  <div className="rsg-id-text">
                    <h3 className="bd-h3">{t('riskScore.overview.phoneTitle')}</h3>
                    <p className="bd-meta">{t('riskScore.overview.phoneSub')}</p>
                  </div>
                </div>
                <span className={chip(deviceData.phoneSMSData.flaggedCount > 0 ? 'danger' : 'mint')}>
                  <span className="dot" />
                  {deviceData.phoneSMSData.flaggedCount > 0 ? t('riskScore.common.threatsDetected') : t('riskScore.common.allClear')}
                </span>
              </div>

              <div className="rsg-stats">
                <StatTile value={deviceData.phoneSMSData.totalCount} label={t('riskScore.common.totalCommunications')} />
                <StatTile value={deviceData.phoneSMSData.flaggedCount} label={t('riskScore.common.flagged')} tone="danger" />
                <StatTile
                  value={`${((deviceData.phoneSMSData.flaggedCount / deviceData.phoneSMSData.totalCount) * 100).toFixed(1)}%`}
                  label={t('riskScore.common.threatRatio')}
                  tone="warn"
                />
              </div>

              <div className="rsg-card-bd">
                {/* Threat Type Breakdown */}
                <div className="rsg-block">
                  <h4 className="rsg-block-title"><ListFilter size={16} strokeWidth={1.9} />{t('riskScore.overview.threatTypeBreakdown')}</h4>
                  <div className="rsg-grid-2 tight">
                    {devicesRiskReport?.report?.statistics?.scam?.map((
                      scamItem: { type: string; count: number },
                      index: number
                    ) => (
                      <div key={index} className="rsg-nested rsg-split">
                        <span className="rsg-dist-name">
                          <span className="rsg-dot" style={{ background: getThreatColor(scamItem.type) }} />
                          <span>{threatTypeLabel(scamItem.type)}</span>
                        </span>
                        <span className="rsg-end">
                          <span className="bd-mono rsg-dist-val" style={{ color: getThreatColor(scamItem.type) }}>
                            {Number(scamItem.count)}
                          </span>
                          <span className="bd-meta bd-mono">{scamItem.count.toFixed(1)}%</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Risk Assessment */}
                <div className={`rsg-nested rsg-level-card rsg-t-${phoneTone}`}>
                  <h5 className="rsg-block-title sm"><MessageSquare size={15} strokeWidth={1.9} />{t('riskScore.overview.commRiskLevel')}</h5>
                  <span className="rsg-level rsg-tone-text">
                    {deviceData.phoneSMSData.flaggedCount === 0 ? t('riskScore.riskLabels.low') :
                      deviceData.phoneSMSData.flaggedCount < 3 ? t('riskScore.riskLabels.medium') : t('riskScore.riskLabels.high')}
                  </span>
                  <span className="bd-meta">{t('riskScore.overview.flaggedComms', { count: deviceData.phoneSMSData.flaggedCount })}</span>
                  <span className={chip(phoneTone)}>
                    <span className="dot" />
                    {deviceData.phoneSMSData.flaggedCount === 0 ? t('riskScore.common.protected') :
                      deviceData.phoneSMSData.flaggedCount < 3 ? t('riskScore.common.monitor') : t('riskScore.common.actionRequired')}
                  </span>
                </div>

                {/* Additional Insights */}
                <Callout tone="info" icon={<Info size={17} strokeWidth={1.9} />} title={t('riskScore.common.securityInsight')}>
                  {deviceData.phoneSMSData.flaggedCount > 0
                    ? t('riskScore.overview.phoneInsightThreats', { count: deviceData.phoneSMSData.flaggedCount })
                    : t('riskScore.overview.phoneInsightClear')
                  }
                </Callout>
              </div>
            </section>

            {/* Combined Security & Vulnerability Overview Card */}
            <section className="bd-card rsg-card rsg-span-all">
              <div className="rsg-card-hd">
                <div className="rsg-card-id">
                  <span className="rsg-ico rsg-t-danger"><ShieldAlert size={18} strokeWidth={1.9} /></span>
                  <div className="rsg-id-text">
                    <h3 className="bd-h3">{t('riskScore.overview.secTitle')}</h3>
                    <p className="bd-meta">{t('riskScore.overview.secSub')}</p>
                  </div>
                </div>
                <span className={chip(secTone)}>
                  <span className="dot" />
                  {deviceData.riskScore.cvePercentage > 70 || deviceData.cveData.criticalCount > 0
                    ? t('riskScore.riskLabels.critical')
                    : deviceData.riskScore.cvePercentage > 40 || deviceData.cveData.highCount > 0
                      ? t('riskScore.riskLabels.high')
                      : t('riskScore.riskLabels.low')}
                </span>
              </div>

              <div className="rsg-stats">
                <StatTile value={`${deviceData.riskScore.cvePercentage}%`} label={t('riskScore.overview.appRiskScore')} tone={permTone} />
                <StatTile value={devicesRiskReport.report.statistics.app.critical.count} label={t('riskScore.overview.criticalCves')} tone="critical" />
                <StatTile value={devicesRiskReport.report.statistics.app.high.count} label={t('riskScore.overview.highCves')} tone="danger" />
                <StatTile value={`${100 - deviceData.riskScore.cvePercentage}%`} label={t('riskScore.overview.securityScore')} />
              </div>

              <div className="rsg-card-bd">
                {/* Combined Risk Assessment */}
                <div className="rsg-block">
                  <h4 className="rsg-block-title"><TriangleAlert size={16} strokeWidth={1.9} />{t('riskScore.overview.combinedAssessment')}</h4>
                  <div className={`rsg-nested rsg-t-${secTone}`}>
                    <div className="rsg-split rsg-gap-b">
                      <div className="rsg-id-text">
                        <h5 className="bd-h3">{t('riskScore.overview.overallSecurityRisk')}</h5>
                        <span className="bd-meta">{t('riskScore.overview.overallSecurityRiskSub')}</span>
                      </div>
                      <div className="rsg-end">
                        <span className="bd-stat-num rsg-num-md rsg-tone-text">
                          {Math.max(deviceData.riskScore.cvePercentage, deviceData.cveData.riskScore)}%
                        </span>
                        <span className="bd-meta">{t('riskScore.overview.combinedRiskScore')}</span>
                      </div>
                    </div>
                    <div className="bd-progress rsg-progress-md">
                      <i
                        style={{
                          width: `${Math.max(deviceData.riskScore.cvePercentage, deviceData.cveData.riskScore)}%`,
                          background: toneVar[secTone],
                        }}
                      />
                    </div>
                    <div className="rsg-split bd-meta rsg-gap-t">
                      <span>{t('riskScore.riskLabels.low')}</span>
                      <span>{t('riskScore.riskLabels.critical')}</span>
                    </div>
                  </div>
                </div>

                {/* CVE and App Risk */}
                <div className="rsg-grid-2">
                  <div className="rsg-nested">
                    <h5 className="rsg-block-title sm"><Bug size={15} strokeWidth={1.9} />{t('riskScore.overview.cveSeverityBreakdown')}</h5>
                    <div className="rsg-stack tight">
                      {[
                        { level: 'critical', count: devicesRiskReport.report.statistics.app.critical.count, color: toneVar.critical },
                        { level: 'high', count: devicesRiskReport.report.statistics.app.high.count, color: toneVar.danger },
                        { level: 'medium', count: devicesRiskReport.report.statistics.app.medium.count, color: toneVar.warn },
                        { level: 'low', count: devicesRiskReport.report.statistics.app.low.count, color: toneVar.mint }
                      ].map(({ level, count, color }) => {
                        const percentage = (devicesRiskReport.report.statistics.app.critical.count +
                          devicesRiskReport.report.statistics.app.high.count +
                          devicesRiskReport.report.statistics.app.medium.count +
                          devicesRiskReport.report.statistics.app.low.count) > 0
                          ? (count / (devicesRiskReport.report.statistics.app.critical.count +
                            devicesRiskReport.report.statistics.app.high.count +
                            devicesRiskReport.report.statistics.app.medium.count +
                            devicesRiskReport.report.statistics.app.low.count)) * 100
                          : 0;

                        return (
                          <div key={level} className="rsg-kv-row rsg-row-card">
                            <span className="rsg-dist-name">
                              <span className="rsg-dot" style={{ background: color }} />
                              <span>{levelLabel(level)}</span>
                            </span>
                            <span className="rsg-end">
                              <span className="bd-mono rsg-strong" style={{ color }}>{count}</span>
                              <span className="bd-meta bd-mono">{percentage.toFixed(1)}%</span>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="rsg-nested">
                    <h5 className="rsg-block-title sm"><AppWindow size={15} strokeWidth={1.9} />{t('riskScore.tabs.logs')}</h5>
                    <div className="rsg-stack tight">
                      <div className="rsg-kv-row rsg-row-card">
                        <span>{t('riskScore.overview.permissionRisk')}</span>
                        <span className={`bd-mono rsg-strong ${toneText(permTone)}`}>{deviceData.riskScore.cvePercentage}%</span>
                      </div>
                      <div className="rsg-kv-row rsg-row-card">
                        <span>{t('riskScore.overview.behaviorMonitoring')}</span>
                        <span className="rsg-strong rsg-c-info">{t('riskScore.common.active')}</span>
                      </div>
                      <div className="rsg-kv-row rsg-row-card">
                        <span>{t('riskScore.overview.securityCompliance')}</span>
                        <span className={`rsg-strong ${toneText(permTone)}`}>
                          {deviceData.riskScore.cvePercentage > 70 ? t('riskScore.overview.compliance.failed') :
                            deviceData.riskScore.cvePercentage > 40 ? t('riskScore.overview.compliance.warning') : t('riskScore.overview.compliance.passed')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Threat Level & Recommendations */}
                <div className="rsg-grid-2">
                  <div className={`rsg-nested rsg-level-card rsg-t-${secThreatTone}`}>
                    <h5 className="rsg-block-title sm"><TriangleAlert size={15} strokeWidth={1.9} />{t('riskScore.common.threatAssessment')}</h5>
                    <span className="rsg-level rsg-tone-text">
                      {deviceData.riskScore.cvePercentage > 70 || deviceData.cveData.criticalCount > 0 ? t('riskScore.riskLabels.critical') :
                        deviceData.riskScore.cvePercentage > 40 || deviceData.cveData.highCount > 0 ? t('riskScore.riskLabels.high') :
                          deviceData.cveData.mediumCount > 0 ? t('riskScore.riskLabels.medium') : t('riskScore.riskLabels.low')}
                    </span>
                    <span className="bd-meta">
                      {deviceData.cveData.criticalCount > 0 && t('riskScore.overview.criticalVulns', { count: deviceData.cveData.criticalCount })}
                      {deviceData.cveData.criticalCount === 0 && deviceData.cveData.highCount > 0 && t('riskScore.overview.highVulns', { count: deviceData.cveData.highCount })}
                      {deviceData.cveData.criticalCount === 0 && deviceData.cveData.highCount === 0 && deviceData.cveData.mediumCount > 0 && t('riskScore.overview.mediumVulns', { count: deviceData.cveData.mediumCount })}
                      {deviceData.cveData.totalCount === 0 && t('riskScore.overview.noVulns')}
                    </span>
                    <span className={chip(secThreatTone)}>
                      <span className="dot" />
                      {deviceData.riskScore.cvePercentage > 70 || deviceData.cveData.criticalCount > 0 ? t('riskScore.overview.secAction.immediate') :
                        deviceData.riskScore.cvePercentage > 40 || deviceData.cveData.highCount > 0 ? t('riskScore.overview.secAction.priority') :
                          deviceData.cveData.mediumCount > 0 ? t('riskScore.overview.secAction.schedule') : t('riskScore.overview.secAction.secure')}
                    </span>
                  </div>

                  <div className="rsg-nested">
                    <h5 className="rsg-block-title sm"><CircleCheck size={15} strokeWidth={1.9} />{t('riskScore.overview.recommendedActions')}</h5>
                    <ul className="rsg-bullets">
                      {deviceData.cveData.criticalCount > 0 && (
                        <li className="rsg-t-danger"><span className="rsg-dot" />{t('riskScore.overview.actions.patchCritical')}</li>
                      )}
                      {deviceData.cveData.highCount > 0 && (
                        <li className="rsg-t-warn"><span className="rsg-dot" />{t('riskScore.overview.actions.updateHighRisk')}</li>
                      )}
                      {deviceData.riskScore.cvePercentage > 40 && (
                        <li className="rsg-t-info"><span className="rsg-dot" />{t('riskScore.overview.actions.reviewPermissions')}</li>
                      )}
                      <li className="rsg-t-mint"><span className="rsg-dot" />{t('riskScore.overview.actions.runScan')}</li>
                    </ul>
                  </div>
                </div>

                {/* Combined Security Insights */}
                <Callout tone="warn" icon={<Info size={17} strokeWidth={1.9} />} title={t('riskScore.common.securityInsight')}>
                  {deviceData.cveData.criticalCount > 0
                    ? t('riskScore.overview.secInsight.critical')
                    : deviceData.cveData.highCount > 0
                      ? t('riskScore.overview.secInsight.high')
                      : deviceData.riskScore.cvePercentage > 40
                        ? t('riskScore.overview.secInsight.elevated')
                        : t('riskScore.overview.secInsight.stable')
                  }
                </Callout>
              </div>
            </section>
          </div>
        )}

        {/* DNS Tab */}
        {activeTab === 'dns' && (
          <section className="bd-card rsg-card">
            <div className="rsg-card-hd">
              <div className="rsg-card-id">
                <span className="rsg-ico rsg-t-info"><Globe size={18} strokeWidth={1.9} /></span>
                <div className="rsg-id-text">
                  <h3 className="bd-h3">{t('riskScore.dns.title')}</h3>
                  <p className="bd-meta">{t('riskScore.dns.subtitle')}</p>
                </div>
              </div>
              <span className={chip(devicesRiskReport?.report?.boosted?.dns?.some((item: any) => item.riskScore > 0) ? 'danger' : 'mint')}>
                <span className="dot" />
                {devicesRiskReport?.report?.boosted?.dns?.some((item: any) => item.riskScore > 0)
                  ? t('riskScore.common.threatsDetected')
                  : t('riskScore.common.allClear')}
              </span>
            </div>

            <div className="rsg-stats">
              <StatTile value={devicesRiskReport?.report?.boosted?.dns?.length.toLocaleString(locale) || 0} label={t('riskScore.common.totalQueries')} />
              <StatTile
                value={devicesRiskReport?.report?.boosted?.dns?.filter((item: any) => item.riskScore > 0).length.toLocaleString(locale) || 0}
                label={t('riskScore.common.suspicious')}
                tone="danger"
              />
              <StatTile
                value={`${devicesRiskReport?.report?.boosted?.dns?.length > 0
                  ? ((devicesRiskReport.report.boosted.dns.filter((item: any) => item.riskScore === 0).length /
                    devicesRiskReport.report.boosted.dns.length) * 100).toFixed(1)
                  : '0'}%`}
                label={t('riskScore.common.safeRate')}
                tone="mint"
              />
              <StatTile
                value={`${devicesRiskReport?.report?.boosted?.dns?.length > 0
                  ? ((devicesRiskReport.report.boosted.dns.filter((item: any) => item.riskScore > 0).length /
                    devicesRiskReport.report.boosted.dns.length) * 100).toFixed(1)
                  : '0'}%`}
                label={t('riskScore.common.threatRatio')}
                tone="warn"
              />
            </div>

            <div className="rsg-card-bd">
              <div className="rsg-split">
                <h4 className="rsg-block-title flush"><ListFilter size={16} strokeWidth={1.9} />{t('riskScore.dns.queryDetails')}</h4>
                <span className="bd-chip bd-mono">{t('riskScore.common.records', { count: devicesRiskReport?.report?.boosted?.dns?.length || 0 })}</span>
              </div>

              <div className="rsg-stack">
                {devicesRiskReport?.report?.boosted?.dns?.map((record: any, index: number) => {
                  const isSuspicious = record.riskScore > 0;
                  const threatLevel = record.riskScore > 75 ? 'high' : record.riskScore > 50 ? 'medium' : record.riskScore > 25 ? 'low' : 'safe';

                  return (
                    <div key={index} className={`rsg-nested rsg-record rsg-t-${isSuspicious ? 'danger' : 'mint'}`}>
                      <div className="rsg-record-main">
                        <span className="rsg-ico"><Globe size={17} strokeWidth={1.9} /></span>
                        <div className="rsg-record-text">
                          <div className="rsg-inline">
                            <span className="bd-mono rsg-strong bd-truncate rsg-record-name">{record.fqdn}</span>
                            <span className={chip(threatTone(record.type || 'unknown'))}>
                              {threatTypeLabel(record.type || 'unknown').toLocaleUpperCase(locale)}
                            </span>
                            {threatLevel !== 'safe' && (
                              <span className={chip(levelTone(threatLevel))}>{levelLabel(threatLevel).toLocaleUpperCase(locale)}</span>
                            )}
                          </div>
                          <div className="rsg-meta-row bd-meta">
                            <span>
                              <Clock size={13} strokeWidth={1.9} />
                              <span className="bd-mono">{record.lasttimestamp ? fmtShort(record.lasttimestamp) : t('riskScore.common.na')}</span>
                            </span>
                            <span>
                              <Zap size={13} strokeWidth={1.9} />
                              <Trans t={t} i18nKey="riskScore.dns.requests" count={record.count} values={{ count: record.count }} components={mono} />
                            </span>
                            {record.dataSentPercent > 0 && (
                              <span className="rsg-c-warn">
                                <ArrowUp size={13} strokeWidth={1.9} />
                                <Trans t={t} i18nKey="riskScore.dns.dataSent" values={{ value: record.dataSentPercent }} components={mono} />
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="rsg-record-score">
                        <div className="rsg-end">
                          <span className={`bd-mono rsg-score-val${record.riskScore > 50 ? ' rsg-c-danger' : ''}`}>{record.riskScore}%</span>
                          <span className="bd-meta">{t('riskScore.common.riskScore')}</span>
                        </div>
                        <span className={`rsg-score-badge bd-mono rsg-t-${scoreTone(record.riskScore)}`}>{record.riskScore}</span>
                      </div>
                    </div>
                  );
                })}

                {/* Empty State */}
                {(!devicesRiskReport?.report?.boosted?.dns || devicesRiskReport.report.boosted.dns.length === 0) && (
                  <div className="rsg-empty">
                    <span className="rsg-ico lg rsg-t-neutral"><Globe size={24} strokeWidth={1.75} /></span>
                    <h4 className="bd-h3">{t('riskScore.dns.emptyTitle')}</h4>
                    <p className="bd-meta">{t('riskScore.dns.emptyText')}</p>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Phone / SMS Tab */}
        {activeTab === 'phone' && (
          <section className="bd-card rsg-card">
            <div className="rsg-card-hd">
              <div className="rsg-card-id">
                <span className="rsg-ico rsg-t-violet"><MessageSquareWarning size={18} strokeWidth={1.9} /></span>
                <div className="rsg-id-text">
                  <h3 className="bd-h3">{t('riskScore.phone.title')}</h3>
                  <p className="bd-meta">{t('riskScore.phone.subtitle')}</p>
                </div>
              </div>
              <span className={chip(devicesRiskReport?.report?.boosted?.scam?.some((item: any) =>
                item.type.includes('SuspectScam') || item.type.includes('Scam') || item.type.includes('Spam')
              ) ? 'danger' : 'mint')}>
                <span className="dot" />
                {devicesRiskReport?.report?.boosted?.scam?.some((item: any) =>
                  item.type.includes('SuspectScam') || item.type.includes('Scam') || item.type.includes('Spam')
                )
                  ? t('riskScore.common.threatsDetected')
                  : t('riskScore.common.allClear')}
              </span>
            </div>

            <div className="rsg-stats">
              <StatTile
                value={devicesRiskReport?.report?.boosted?.scam?.reduce((total: number, item: any) => total + (item.count || 0), 0) || 0}
                label={t('riskScore.common.totalCommunications')}
              />
              <StatTile
                value={devicesRiskReport?.report?.boosted?.scam?.reduce((total: number, item: any) => {
                  const hasScam = item.type.includes('SuspectScam') || item.type.includes('Scam');
                  return total + (hasScam ? item.count : 0);
                }, 0) || 0}
                label={t('riskScore.common.flagged')}
                tone="danger"
              />
              <StatTile
                value={`${devicesRiskReport?.report?.boosted?.scam?.length > 0
                  ? Math.min(100, Math.round(
                    (devicesRiskReport.report.boosted.scam.reduce((total: number, item: any) => {
                      const scamCount = item.type.filter((t: string) =>
                        t.includes('SuspectScam') || t.includes('Scam')
                      ).length;
                      return total + (scamCount * 25); // Each scam type adds 25% risk
                    }, 0) / devicesRiskReport.report.boosted.scam.length)
                  ))
                  : 0}%`}
                label={t('riskScore.common.riskScore')}
                tone="warn"
              />
            </div>

            <div className="rsg-card-bd">
              <div className="rsg-split">
                <h4 className="rsg-block-title flush"><MessageSquare size={16} strokeWidth={1.9} />{t('riskScore.phone.commDetails')}</h4>
                <span className="bd-chip bd-mono">{t('riskScore.common.records', { count: devicesRiskReport?.report?.boosted?.scam?.length || 0 })}</span>
              </div>

              <div className="rsg-stack">
                {devicesRiskReport?.report?.boosted?.scam?.map((record: any, index: number) => {
                  // Determine threat level based on types
                  const hasScam = record.type.includes('SuspectScam') || record.type.includes('Scam');
                  const hasSpam = record.type.includes('Spam');
                  const flagged = hasScam || hasSpam;

                  // Calculate risk score based on types
                  const scamCount = record.type.filter((t: string) =>
                    t.includes('SuspectScam') || t.includes('Scam')
                  ).length;
                  const spamCount = record.type.filter((t: string) => t.includes('Spam')).length;
                  const riskScore = Math.min(100, Math.round(
                    (scamCount * 30) + (spamCount * 10)
                  ));

                  // Determine threat type
                  const threatType = hasScam ? 'scam' : hasSpam ? 'spam' : 'normal';

                  // Threat level for severity
                  const threatLevel = riskScore > 75 ? 'high' :
                    riskScore > 50 ? 'medium' :
                      riskScore > 25 ? 'low' : 'safe';

                  return (
                    <div key={index} className={`rsg-nested rsg-record top rsg-t-${flagged ? 'danger' : 'mint'}`}>
                      <div className="rsg-record-text">
                        <div className="rsg-inline">
                          <span className="bd-mono rsg-strong rsg-record-number">{record.number}</span>
                          <span className={chip(threatTone(threatType))}>{threatTypeLabel(threatType).toLocaleUpperCase(locale)}</span>
                          {threatLevel !== 'safe' && (
                            <span className={chip(levelTone(threatLevel))}>{levelLabel(threatLevel).toLocaleUpperCase(locale)}</span>
                          )}
                        </div>

                        {/* Type Tags */}
                        <div className="rsg-inline">
                          {record.type.map((type: string, idx: number) => (
                            <span
                              key={idx}
                              className={chip(type.includes('SuspectScam') || type.includes('Scam')
                                ? 'danger'
                                : type.includes('Spam')
                                  ? 'warn'
                                  : 'neutral')}
                            >
                              {threatTypeLabel(type)}
                            </span>
                          ))}
                        </div>

                        {/* Metadata */}
                        <div className="rsg-meta-row bd-meta">
                          <span>
                            <Clock size={13} strokeWidth={1.9} />
                            <span className="bd-mono">{record.lasttimestamp ? fmtShort(record.lasttimestamp) : t('riskScore.common.na')}</span>
                          </span>
                          <span>
                            <Zap size={13} strokeWidth={1.9} />
                            <Trans t={t} i18nKey="riskScore.phone.attempts" count={record.count} values={{ count: record.count }} components={mono} />
                          </span>
                        </div>

                        {/* Analysis Summary */}
                        <div className="rsg-analysis">
                          <span className="rsg-strong-sm">{t('riskScore.phone.threatAnalysis')}</span>
                          <span className="rsg-analysis-text">
                            {flagged
                              ? <TriangleAlert size={14} strokeWidth={1.9} className="rsg-c-warn" />
                              : <CircleCheck size={14} strokeWidth={1.9} className="rsg-c-mint" />}
                            {hasScam && hasSpam ? (
                              t('riskScore.phone.analysis.both')
                            ) : hasScam ? (
                              t('riskScore.phone.analysis.scam')
                            ) : hasSpam ? (
                              t('riskScore.phone.analysis.spam')
                            ) : (
                              t('riskScore.phone.analysis.none')
                            )}
                          </span>
                          <span className="bd-meta">
                            {t('riskScore.phone.flaggedTypes', { scam: t('riskScore.phone.scamTypes', { count: scamCount }), spam: t('riskScore.phone.spamTypes', { count: spamCount }) })}
                          </span>
                        </div>
                      </div>

                      <div className="rsg-record-score">
                        <div className="rsg-end">
                          <span className={`bd-mono rsg-score-val${riskScore > 50 ? ' rsg-c-danger' : ''}`}>{riskScore}%</span>
                          <span className="bd-meta">{t('riskScore.common.riskScore')}</span>
                        </div>
                        <span className={`rsg-score-badge lg bd-mono rsg-t-${scoreTone(riskScore)}`}>{riskScore}</span>
                      </div>
                    </div>
                  );
                })}

                {/* Empty State */}
                {(!devicesRiskReport?.report?.boosted?.scam || devicesRiskReport.report.boosted.scam.length === 0) && (
                  <div className="rsg-empty">
                    <span className="rsg-ico lg rsg-t-neutral"><MessageSquare size={24} strokeWidth={1.75} /></span>
                    <h4 className="bd-h3">{t('riskScore.phone.emptyTitle')}</h4>
                    <p className="bd-meta">{t('riskScore.phone.emptyText')}</p>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* App Risk Analysis Tab (shared component) */}
        {activeTab === 'logs' && (
          <AppRiskAnalysis
            boostedApps={devicesRiskReport?.report?.boosted?.app}
          />
        )}

        {/* Health Dashboard Tab */}
        {activeTab === 'health' && (
          <div className="health-dashboard">
            <section className="bd-card rsg-card">
              <div className="rsg-card-hd">
                <div className="rsg-card-id">
                  <span className="rsg-ico lg rsg-t-mint"><ShieldCheck size={22} strokeWidth={1.9} /></span>
                  <div className="rsg-id-text">
                    <h3 className="bd-h2">{t('riskScore.health.title')}</h3>
                    <p className="bd-meta">{t('riskScore.health.subtitle')}</p>
                  </div>
                </div>
                <span className={chip(healthTone)}>
                  <span className="dot" />
                  <Trans t={t} i18nKey="riskScore.health.overallHealth" values={{ score: healthScore }} components={mono} />
                </span>
              </div>

              <div className="rsg-card-bd">
                <div className="rsg-hero-grid">
                  {/* Score hero */}
                  <div className="rsg-nested rsg-hero">
                    <div className="rsg-hero-main">
                      <span className="bd-field-label rsg-flush">{t('riskScore.health.overallHealthScore')}</span>
                      <div className="rsg-hero-score">
                        {(() => {
                          const calculatedValue = 100 - (devicesRiskReport?.report?.scores?.overall || 0);
                          const safeValue = Math.max(0, calculatedValue);
                          const healthScore = devicesRiskReport?.report?.scores?.overall || 0;

                          return (
                            <span
                              className={`bd-stat-num rsg-hero-num ${healthScore >= 90
                                ? 'rsg-c-mint'
                                : healthScore >= 70
                                  ? 'rsg-c-warn'
                                  : healthScore >= 50
                                    ? 'rsg-c-warn'
                                    : 'rsg-c-danger'
                              }`}
                            >
                              <CountUp
                                end={safeValue}
                                duration={2}
                                preserveValue={true}
                                useEasing={true}
                              />%
                            </span>
                          );
                        })()}
                        <span className="bd-meta">{t('riskScore.health.outOf100')}</span>
                      </div>

                      <div className="rsg-inline">
                        <span className={chip(overallTone)}>
                          <span className="dot" />
                          {(100 - devicesRiskReport?.report?.scores?.overall) >= 80 ? t('riskScore.health.rating.excellent') :
                            (100 - devicesRiskReport?.report?.scores?.overall) >= 60 ? t('riskScore.health.rating.good') :
                              (100 - devicesRiskReport?.report?.scores?.overall) >= 40 ? t('riskScore.health.rating.fair') : t('riskScore.health.rating.poor')}
                        </span>
                        <span className={`rsg-trend bd-mono ${riskChange > 0 ? 'rsg-c-danger' : riskChange < 0 ? 'rsg-c-mint' : 'rsg-c-mute'}`}>
                          {riskChange > 0
                            ? <TrendingUp size={14} strokeWidth={2} />
                            : riskChange < 0
                              ? <TrendingDown size={14} strokeWidth={2} />
                              : <Minus size={14} strokeWidth={2} />}
                          {t('riskScore.health.riskChange', { change: `${riskChange > 0 ? '+' : ''}${riskChange}` })}
                        </span>
                      </div>

                      <div className="rsg-hero-device">
                        <span className="bd-meta">{t('riskScore.health.deviceId')}</span>
                        <span className="bd-mono rsg-strong bd-truncate">{devicesRiskReport?.deviceId}</span>
                      </div>
                      <p className="bd-meta rsg-flush">{t('riskScore.health.basedOn')}</p>

                      <div className="rsg-hero-bar">
                        <div className="rsg-split bd-meta">
                          <span>{t('riskScore.health.health')}</span>
                          <span className="bd-mono">{t('riskScore.health.target')}</span>
                        </div>
                        <div
                          className={`bd-progress rsg-progress-lg ${healthScore >= 90
                            ? ''
                            : (100 - devicesRiskReport?.report?.scores?.overall) >= 70
                              ? 'warn'
                              : (100 - devicesRiskReport?.report?.scores?.overall) >= 50
                                ? 'warn'
                                : 'danger'
                          }`}
                        >
                          <i style={{ width: `${(100 - devicesRiskReport?.report?.scores?.overall)}%` }} />
                        </div>
                        <div className="rsg-scale bd-meta">
                          <span>{t('riskScore.health.rating.poor')}</span>
                          <span>{t('riskScore.health.rating.fair')}</span>
                          <span>{t('riskScore.health.rating.good')}</span>
                          <span>{t('riskScore.health.rating.excellent')}</span>
                        </div>
                      </div>
                    </div>

                    {/* Gauge */}
                    <div className="score-item rsg-gauge">
                      <div style={{ width: '220px', height: '170px' }}>
                        <GaugeComponent
                          key={theme}
                          value={Math.max(0, 100 - (devicesRiskReport?.report?.scores?.overall || 0))}
                          type="radial"
                          minValue={0}
                          maxValue={100}
                          labels={{
                            tickLabels: {
                              type: 'outer',
                              ticks: [
                                { value: 0 },
                                { value: 25 },
                                { value: 50 },
                                { value: 75 },
                                { value: 100 }
                              ],
                              defaultTickValueConfig: {
                                style: { fill: ct.mute, fontSize: '10px', fontFamily: fonts.mono, textShadow: 'none' }
                              },
                              defaultTickLineConfig: { color: ct.border }
                            },
                            valueLabel: { hide: true }
                          }}
                          arc={{
                            subArcs: [
                              { limit: 50, color: ct.danger },       // 0–49
                              { limit: 70, color: ct.warn },         // 50–69
                              { limit: 90, color: `${ct.accent}99` }, // 70–89
                              { limit: 100, color: ct.accent }       // 90–100
                            ],
                            padding: 0.02,
                            width: 0.3
                          }}
                          pointer={{
                            elastic: true,
                            type: 'needle',
                            animationDelay: 500,
                            length: 0.7,
                            color: ct.text2,
                            baseColor: ct.text2
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="rsg-nested rsg-quick">
                    <h4 className="bd-h3">{t('riskScore.health.quickStats')}</h4>
                    <div className="rsg-quick-list">
                      <div className="rsg-kv-row">
                        <span className="rsg-quick-label">
                          <span className="rsg-ico sm rsg-t-info"><Zap size={15} strokeWidth={1.9} /></span>
                          {t('riskScore.health.lastScan')}
                        </span>
                        <span className="bd-mono rsg-strong rsg-align-right">
                          {devicesRiskReport?.report?.timestamp ? new Date(devicesRiskReport?.report?.timestamp).toLocaleString(locale, {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          }) : t('riskScore.common.never')}
                        </span>
                      </div>

                      <div className="rsg-kv-row">
                        <span className="rsg-quick-label">
                          <span className="rsg-ico sm rsg-t-mint"><Check size={15} strokeWidth={2} /></span>
                          {t('riskScore.health.protectedItems')}
                        </span>
                        <span className="bd-mono rsg-strong rsg-c-mint">24/26</span>
                      </div>

                      <div className="rsg-kv-row">
                        <span className="rsg-quick-label">
                          <span className="rsg-ico sm rsg-t-violet"><Activity size={15} strokeWidth={1.9} /></span>
                          {t('riskScore.health.uptime')}
                        </span>
                        <span className="bd-mono rsg-strong">99.2%</span>
                      </div>

                      <div className="rsg-kv-row top">
                        <div className="rsg-quick-label">
                          <span className="rsg-ico sm rsg-t-danger"><CircleAlert size={15} strokeWidth={1.9} /></span>
                          <div className="rsg-id-text">
                            <span>{t('riskScore.health.issuesFound')}</span>
                            <button
                              type="button"
                              onClick={() => {
                                const securityPlan = document.querySelector('.recommendations-grid');
                                if (securityPlan) {
                                  securityPlan.scrollIntoView({ behavior: 'smooth' });
                                  // Highlight the Risk Assessment Summary section
                                  const riskSection = document.querySelector('.timeline-item-enhanced[data-section="immediate"]') as HTMLElement;
                                  if (riskSection) {
                                    riskSection.classList.add('expanded');
                                    riskSection.style.animation = 'pulse-highlight 2s';
                                    setTimeout(() => {
                                      riskSection.style.animation = '';
                                    }, 2000);
                                  }
                                }
                              }}
                              className="rsg-link"
                            >
                              {t('riskScore.health.viewInPlan')}
                              <ArrowRight size={12} strokeWidth={2} />
                            </button>
                          </div>
                        </div>
                        <div className="rsg-end">
                          <span className="bd-mono rsg-issue-val rsg-c-danger">
                            {devicesRiskReport?.report?.scores?.boostedIncrees > 0 ? `+ ${devicesRiskReport.report.scores.boostedIncrees}%` : '0%'}
                          </span>
                          <div className="rsg-inline end">
                            {devicesRiskReport?.report?.scores?.boosted?.app > 0 && (
                              <button
                                type="button"
                                onClick={() => handleQuickFix('critical')}
                                className="rsg-mini-btn rsg-t-danger"
                                title={t('riskScore.health.fixCriticalTitle')}
                              >
                                {t('riskScore.health.appsBtn', { value: devicesRiskReport?.report?.scores?.boosted?.app })}
                              </button>
                            )}
                            {devicesRiskReport?.report?.scores?.boosted?.dns > 0 && (
                              <button
                                type="button"
                                onClick={() => handleQuickFix('network')}
                                className="rsg-mini-btn rsg-t-warn"
                                title={t('riskScore.health.fixNetworkTitle')}
                              >
                                {t('riskScore.health.networkBtn', { value: devicesRiskReport?.report?.scores?.boosted?.dns })}
                              </button>
                            )}
                            {devicesRiskReport?.report?.scores?.boosted?.scam > 0 && (
                              <button
                                type="button"
                                onClick={() => handleQuickFix('communication')}
                                className="rsg-mini-btn rsg-t-violet"
                                title={t('riskScore.health.fixCommTitle')}
                              >
                                {t('riskScore.health.spamBtn', { value: devicesRiskReport?.report?.scores?.boosted?.scam })}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <div className="recommendations-grid">
              {/* Personalized Security Plan */}
              <section className="bd-card rsg-card">
                <div className="rsg-card-bd">
                  {/* Issues Overview Banner */}
                  <div className="rsg-callout rsg-callout-row rsg-t-danger">
                    <div className="rsg-card-id">
                      <span className="rsg-ico lg rsg-badge-wrap">
                        <CircleAlert size={20} strokeWidth={1.9} />
                        <span className="rsg-count-badge bd-mono">{totalIssues}</span>
                      </span>
                      <div className="rsg-id-text">
                        <h4 className="bd-h3">{t('riskScore.plan.activeIssuesTitle')}</h4>
                        <p className="bd-body rsg-flush">
                          {t('riskScore.plan.activeIssuesText', { critical: deviceData.cveData.criticalCount, network: deviceData.dnsData.suspiciousCount, communication: deviceData.phoneSMSData.flaggedCount })}
                        </p>
                      </div>
                    </div>
                    <div className="rsg-actions">
                      <button type="button" onClick={() => handleQuickFix('critical')} className="bd-btn danger-soft">
                        <Check size={16} strokeWidth={2} />
                        {t('riskScore.plan.fixCriticalFirst')}
                      </button>
                      <button type="button" onClick={() => handleQuickFix('all')} className="bd-btn primary">
                        {t('riskScore.common.fixAllIssues')}
                      </button>
                    </div>
                  </div>

                  {/* Risk Assessment Summary */}
                  <div className="rsg-nested rsg-callout-row rsg-t-warn">
                    <div className="rsg-card-id">
                      <span className="rsg-ico lg"><TriangleAlert size={20} strokeWidth={1.9} /></span>
                      <div className="rsg-id-text">
                        <h4 className="bd-h3">{t('riskScore.plan.summaryTitle')}</h4>
                        <p className="bd-meta rsg-flush">{t('riskScore.plan.summarySub')}</p>
                      </div>
                    </div>
                    <div className="rsg-summary-side">
                      <div className="rsg-end">
                        <span className="bd-stat-num rsg-num-md">{totalIssues}</span>
                        <span className="bd-meta">{t('riskScore.plan.totalIssues')}</span>
                      </div>
                      <div className="rsg-ring">
                        <svg viewBox="0 0 100 100" aria-hidden="true">
                          <circle cx="50" cy="50" r="40" fill="none" style={{ stroke: 'var(--surface-3)' }} strokeWidth="8" />
                          <circle
                            cx="50" cy="50" r="40" fill="none"
                            style={{ stroke: 'var(--danger)' }}
                            strokeWidth="8" strokeLinecap="round"
                            strokeDasharray={`${((deviceData.cveData.criticalCount / (deviceData.cveData.totalCount + deviceData.dnsData.suspiciousCount + deviceData.phoneSMSData.flaggedCount || 1)) * 251)} 251`}
                            transform="rotate(-90 50 50)"
                          />
                        </svg>
                        <span className="rsg-ring-label">
                          {deviceData.cveData.criticalCount > 0 ? t('riskScore.levels.high') : t('riskScore.levels.moderate')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Recommendation Categories */}
                  <div className="rsg-block">
                    <div className="rsg-split wrap">
                      <div className="rsg-inline">
                        <CircleCheck size={18} strokeWidth={1.9} className="rsg-c-mint" />
                        <h4 className="bd-h3">{t('riskScore.plan.recommendedTitle')}</h4>
                        <span className="bd-chip bd-mono">{t('riskScore.plan.items', { count: remediationActions.length })}</span>
                      </div>
                      <div className="rsg-actions">
                        <button type="button" className="bd-btn ghost rsg-btn-sm">
                          <ArrowDownWideNarrow size={15} strokeWidth={1.9} />
                          {t('riskScore.plan.sortByPriority')}
                        </button>
                        <button type="button" className="bd-btn secondary rsg-btn-sm">
                          <ClipboardList size={15} strokeWidth={1.9} />
                          {t('riskScore.plan.exportPlan')}
                        </button>
                      </div>
                    </div>

                    <div className="rsg-rec-grid">
                      {/* App Security Card */}
                      {deviceData.riskScore.cvePercentage > 40 && (
                        <div className="rsg-rec rsg-t-critical">
                          <div className="rsg-rec-hd">
                            <div className="rsg-card-id top">
                              <span className="rsg-ico lg"><AppWindow size={20} strokeWidth={1.9} /></span>
                              <div className="rsg-id-text">
                                <h5 className="bd-h3">{t('riskScore.plan.app.title')}</h5>
                                <div className="rsg-inline">
                                  <span className={chip('critical')}><CircleAlert size={12} strokeWidth={2} />{t('riskScore.plan.app.priority')}</span>
                                  <span className="bd-meta">{t('riskScore.common.estimatedTime')} <span className="bd-mono">{t('riskScore.common.minutesShort', { count: 45 })}</span></span>
                                </div>
                              </div>
                            </div>
                            <div className="rsg-end">
                              <span className="bd-mono rsg-rec-val rsg-tone-text">{devicesRiskReport?.report?.scores?.app || 0}%</span>
                              <span className="bd-meta">{t('riskScore.common.riskScore')}</span>
                              <button type="button" onClick={() => handleQuickFix('app')} className="rsg-mini-btn">
                                {t('riskScore.plan.app.fix')}
                              </button>
                            </div>
                          </div>

                          <p className="bd-body rsg-flush">
                            {devicesRiskReport?.report?.statistics?.app?.critical?.count > 0
                              ? t('riskScore.plan.app.critical', { count: devicesRiskReport?.report?.statistics?.app?.critical?.count })
                              : t('riskScore.plan.app.elevated', { count: devicesRiskReport?.report?.statistics?.app?.high?.count })
                            }
                          </p>

                          <div className="rsg-kv">
                            <div className="rsg-kv-row">
                              <span className="rsg-kv-label"><Smartphone size={14} strokeWidth={1.9} />{t('riskScore.plan.app.affectedApps')}</span>
                              <span className="bd-mono rsg-strong">{t('riskScore.plan.app.appsCount', { count: deviceData.cveData.criticalCount + deviceData.cveData.highCount })}</span>
                            </div>
                            <div className="rsg-kv-row">
                              <span className="rsg-kv-label"><CalendarClock size={14} strokeWidth={1.9} />{t('riskScore.plan.app.lastAudit')}</span>
                              <span className="rsg-strong">{t('riskScore.plan.app.lastAuditValue')}</span>
                            </div>
                            <div className="rsg-kv-row">
                              <span className="rsg-kv-label"><ShieldAlert size={14} strokeWidth={1.9} />{t('riskScore.plan.app.securityImpact')}</span>
                              <span className="rsg-strong rsg-c-danger">{t('riskScore.levels.high')}</span>
                            </div>
                          </div>

                          <ul className="rsg-bullets">
                            <li className="rsg-t-danger"><span className="rsg-dot" />{t('riskScore.plan.app.bullets.update')}</li>
                            <li className="rsg-t-warn"><span className="rsg-dot" />{t('riskScore.plan.app.bullets.restrict')}</li>
                            <li className="rsg-t-warn"><span className="rsg-dot" />{t('riskScore.plan.app.bullets.remove')}</li>
                          </ul>

                          <div className="rsg-rec-ft">
                            <button type="button" className="bd-btn danger-soft rsg-grow" onClick={() => handleQuickFix('app')}>
                              <Check size={16} strokeWidth={2} />
                              {t('riskScore.plan.app.startScan')}
                            </button>
                            <button type="button" className="bd-iconbtn rsg-iconbtn" aria-label={t('riskScore.common.moreInformation')}>
                              <Info size={16} strokeWidth={1.9} />
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Network Security Card */}
                      {deviceData.dnsData.suspiciousCount > 0 && (
                        <div className="rsg-rec rsg-t-danger">
                          <div className="rsg-rec-hd">
                            <div className="rsg-card-id top">
                              <span className="rsg-ico lg"><Network size={20} strokeWidth={1.9} /></span>
                              <div className="rsg-id-text">
                                <h5 className="bd-h3">{t('riskScore.plan.network.title')}</h5>
                                <div className="rsg-inline">
                                  <span className={chip('danger')}><CircleAlert size={12} strokeWidth={2} />{t('riskScore.plan.network.priority')}</span>
                                  <span className="bd-meta">{t('riskScore.common.estimatedTime')} <span className="bd-mono">{t('riskScore.common.minutesShort', { count: 20 })}</span></span>
                                </div>
                              </div>
                            </div>
                            <div className="rsg-end">
                              <span className="bd-mono rsg-rec-val rsg-tone-text">{devicesRiskReport?.report?.scores?.dns || 0}%</span>
                              <span className="bd-meta">{t('riskScore.plan.network.suspiciousQueries')}</span>
                              <button type="button" onClick={() => handleQuickFix('network')} className="rsg-mini-btn">
                                {t('riskScore.plan.network.fix')}
                              </button>
                            </div>
                          </div>

                          <p className="bd-body rsg-flush">
                            {t('riskScore.plan.network.text', { count: devicesRiskReport?.report?.boosted?.dns?.length || 0 })}
                          </p>

                          <div className="rsg-kv">
                            <div className="rsg-kv-row">
                              <span className="rsg-kv-label"><Bug size={14} strokeWidth={1.9} />{t('riskScore.plan.network.malwareRisk')}</span>
                              <span className={`rsg-strong ${deviceData.dnsData.suspiciousCount > 5 ? 'rsg-c-danger' :
                                deviceData.dnsData.suspiciousCount > 2 ? 'rsg-c-warn' : 'rsg-c-mint'
                              }`}>
                                {deviceData.dnsData.suspiciousCount > 5 ? t('riskScore.levels.high') :
                                  deviceData.dnsData.suspiciousCount > 2 ? t('riskScore.levels.medium') : t('riskScore.levels.low')}
                              </span>
                            </div>
                            <div className="rsg-kv-row">
                              <span className="rsg-kv-label"><CalendarClock size={14} strokeWidth={1.9} />{t('riskScore.plan.network.lastScan')}</span>
                              <span className="rsg-strong">{t('riskScore.plan.network.lastScanValue')}</span>
                            </div>
                            <div className="rsg-kv-row">
                              <span className="rsg-kv-label"><ListFilter size={14} strokeWidth={1.9} />{t('riskScore.plan.network.threatCategories')}</span>
                              <span className="bd-mono rsg-strong">
                                {Object.entries(deviceData.dnsData.categories).filter(([cat]) => cat !== 'normal').length}
                              </span>
                            </div>
                          </div>

                          <div className="rsg-rec-ft">
                            <button type="button" className="bd-btn danger-soft rsg-grow" onClick={() => handleQuickFix('network')}>
                              <Check size={16} strokeWidth={2} />
                              {t('riskScore.plan.network.enableDns')}
                            </button>
                            <button type="button" className="bd-iconbtn rsg-iconbtn" aria-label={t('riskScore.common.moreInformation')}>
                              <Info size={16} strokeWidth={1.9} />
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Communication Security Card */}
                      {deviceData.phoneSMSData.flaggedCount > 0 && (
                        <div className="rsg-rec rsg-t-warn">
                          <div className="rsg-rec-hd">
                            <div className="rsg-card-id top">
                              <span className="rsg-ico lg"><MessageSquareWarning size={20} strokeWidth={1.9} /></span>
                              <div className="rsg-id-text">
                                <h5 className="bd-h3">{t('riskScore.plan.comm.title')}</h5>
                                <div className="rsg-inline">
                                  <span className={chip('warn')}><CircleAlert size={12} strokeWidth={2} />{t('riskScore.plan.comm.priority')}</span>
                                  <span className="bd-meta">{t('riskScore.common.estimatedTime')} <span className="bd-mono">{t('riskScore.common.minutesShort', { count: 15 })}</span></span>
                                </div>
                              </div>
                            </div>
                            <div className="rsg-end">
                              <span className="bd-mono rsg-rec-val rsg-tone-text">{devicesRiskReport?.report?.scores?.scam || 0}%</span>
                              <span className="bd-meta">{t('riskScore.plan.comm.flagged')}</span>
                              <button type="button" onClick={() => handleQuickFix('communication')} className="rsg-mini-btn">
                                {t('riskScore.plan.comm.fix')}
                              </button>
                            </div>
                          </div>

                          <p className="bd-body rsg-flush">
                            {t('riskScore.plan.comm.text', { count: devicesRiskReport?.report?.boosted?.scam?.length || 0 })}
                          </p>

                          <div className="rsg-kv">
                            <div className="rsg-kv-row">
                              <span className="rsg-kv-label"><Fingerprint size={14} strokeWidth={1.9} />{t('riskScore.plan.comm.phishingRisk')}</span>
                              <span className="rsg-strong rsg-c-warn">{t('riskScore.levels.medium')}</span>
                            </div>
                            <div className="rsg-kv-row">
                              <span className="rsg-kv-label"><ShieldCheck size={14} strokeWidth={1.9} />{t('riskScore.plan.comm.spamProtection')}</span>
                              <span className="rsg-strong rsg-c-mint">{t('riskScore.common.active')}</span>
                            </div>
                            <div className="rsg-kv-row">
                              <span className="rsg-kv-label"><CalendarClock size={14} strokeWidth={1.9} />{t('riskScore.plan.comm.lastFilterUpdate')}</span>
                              <span className="rsg-strong">{t('riskScore.common.today')}</span>
                            </div>
                          </div>

                          <div className="rsg-rec-ft">
                            <button type="button" className="bd-btn secondary rsg-grow" onClick={() => handleQuickFix('communication')}>
                              <Check size={16} strokeWidth={2} />
                              {t('riskScore.plan.comm.configureFilters')}
                            </button>
                            <button type="button" className="bd-iconbtn rsg-iconbtn" aria-label={t('riskScore.common.moreInformation')}>
                              <Info size={16} strokeWidth={1.9} />
                            </button>
                          </div>
                        </div>
                      )}

                      {/* General Security Best Practices */}
                      <div className="rsg-rec rsg-t-mint">
                        <div className="rsg-rec-hd">
                          <div className="rsg-card-id top">
                            <span className="rsg-ico lg"><ShieldCheck size={20} strokeWidth={1.9} /></span>
                            <div className="rsg-id-text">
                              <h5 className="bd-h3">{t('riskScore.plan.foundation.title')}</h5>
                              <div className="rsg-inline">
                                <span className={chip('mint')}><CircleCheck size={12} strokeWidth={2} />{t('riskScore.plan.foundation.priority')}</span>
                                <span className="bd-meta">{t('riskScore.common.estimatedTime')} <span className="bd-mono">{t('riskScore.common.minutesShort', { count: 30 })}</span></span>
                              </div>
                            </div>
                          </div>
                          <div className="rsg-end">
                            <span className="bd-mono rsg-rec-val rsg-tone-text">100%</span>
                            <span className="bd-meta">{t('riskScore.plan.foundation.coverageGoal')}</span>
                            <button type="button" onClick={handleFixAll} className="rsg-mini-btn">
                              {t('riskScore.plan.foundation.applyAll')}
                            </button>
                          </div>
                        </div>

                        <p className="bd-body rsg-flush">
                          {t('riskScore.plan.foundation.text')}
                        </p>

                        <div className="rsg-kv">
                          <div className="rsg-kv-row">
                            <span className="rsg-kv-label"><KeyRound size={14} strokeWidth={1.9} />{t('riskScore.plan.foundation.passwordStrength')}</span>
                            <span className="rsg-strong rsg-c-mint">{t('riskScore.plan.foundation.strong')}</span>
                          </div>
                          <div className="rsg-kv-row">
                            <span className="rsg-kv-label"><Fingerprint size={14} strokeWidth={1.9} />{t('riskScore.plan.foundation.twoFa')}</span>
                            <span className="rsg-strong rsg-c-info">{t('riskScore.common.enabled')}</span>
                          </div>
                          <div className="rsg-kv-row">
                            <span className="rsg-kv-label"><Lock size={14} strokeWidth={1.9} />{t('riskScore.plan.foundation.encryption')}</span>
                            <span className="rsg-strong rsg-c-mint">{t('riskScore.common.active')}</span>
                          </div>
                        </div>

                        <div className="rsg-rec-ft">
                          <button type="button" className="bd-btn primary rsg-grow" onClick={handleFixAll}>
                            <Check size={16} strokeWidth={2} />
                            {t('riskScore.plan.foundation.applyAllBest')}
                          </button>
                          <button type="button" className="bd-iconbtn rsg-iconbtn" aria-label={t('riskScore.common.moreInformation')}>
                            <Info size={16} strokeWidth={1.9} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Progress Tracking & Timeline */}
                  <div className="rsg-stack">

                    {/* Security Roadmap */}
                    <div className="rsg-nested rsg-roadmap">
                      <div className="rsg-split wrap">
                        <div className="rsg-card-id">
                          <span className="rsg-ico lg rsg-t-info"><Clock size={20} strokeWidth={1.9} /></span>
                          <div className="rsg-id-text">
                            <h5 className="bd-h3">{t('riskScore.roadmap.title')}</h5>
                            <span className="bd-meta">{t('riskScore.roadmap.subtitle')}</span>
                          </div>
                        </div>
                        <div className="rsg-actions">
                          <div className="rsg-menu">
                            <button
                              type="button"
                              className="bd-iconbtn rsg-iconbtn"
                              title={t('riskScore.roadmap.settings')}
                              aria-label={t('riskScore.roadmap.settings')}
                              aria-haspopup="menu"
                            >
                              <Settings size={16} strokeWidth={1.9} />
                            </button>
                            <div className="rsg-menu-pop" role="menu">
                              <button type="button" role="menuitem">{t('riskScore.roadmap.exportRoadmap')}</button>
                              <button type="button" role="menuitem">{t('riskScore.roadmap.share')}</button>
                              <button type="button" role="menuitem">{t('riskScore.roadmap.configureAlerts')}</button>
                            </div>
                          </div>
                          <span className={chip('info')}>
                            <Trans t={t} i18nKey="riskScore.roadmap.activeIssuesChip" count={totalIssues} values={{ count: totalIssues }} components={mono} />
                          </span>
                        </div>
                      </div>

                      {/* Progress Summary */}
                      <div className="rsg-progress-summary">
                        <div className="rsg-split">
                          <span className="rsg-strong-sm">{t('riskScore.roadmap.progressTitle')}</span>
                          <span className="bd-mono rsg-strong">{t('riskScore.roadmap.complete', { value: calculateOverallProgress() })}</span>
                        </div>
                        <div className="bd-progress">
                          <i style={{ width: `${calculateOverallProgress()}%` }} />
                        </div>
                        <div className="rsg-milestones">
                          <div className="rsg-milestone rsg-t-danger">
                            <span className="rsg-milestone-dot">
                              <CircleAlert size={14} strokeWidth={2} />
                              <span className="rsg-tip" role="tooltip">{t('riskScore.roadmap.milestones.immediateTip')}</span>
                            </span>
                            <span className="rsg-milestone-label">{t('riskScore.roadmap.milestones.immediate')}</span>
                          </div>
                          <div className="rsg-milestone rsg-t-warn">
                            <span className="rsg-milestone-dot">
                              <Clock size={14} strokeWidth={2} />
                              <span className="rsg-tip" role="tooltip">{t('riskScore.roadmap.milestones.shortTermTip')}</span>
                            </span>
                            <span className="rsg-milestone-label">{t('riskScore.roadmap.milestones.shortTerm')}</span>
                          </div>
                          <div className="rsg-milestone rsg-t-mint">
                            <span className="rsg-milestone-dot">
                              <CircleCheck size={14} strokeWidth={2} />
                              <span className="rsg-tip" role="tooltip">{t('riskScore.roadmap.milestones.longTermTip')}</span>
                            </span>
                            <span className="rsg-milestone-label">{t('riskScore.roadmap.milestones.longTerm')}</span>
                          </div>
                        </div>
                      </div>

                      <div className="timeline-enhanced rsg-timeline">
                        {/* Vertical Timeline Line */}
                        <div className="rsg-tl-line">
                          <span style={{ height: `${calculateOverallProgress()}%` }} />
                        </div>

                        {/* Timeline Items */}
                        {[
                          {
                            id: 'immediate',
                            title: t('riskScore.roadmap.sections.immediate.title'),
                            timeframe: t('riskScore.roadmap.sections.immediate.timeframe'),
                            color: 'red',
                            count: deviceData.cveData?.criticalCount || 0,
                            icon: 'alert',
                            expanded: true,
                            data: deviceData.cveData
                          },
                          {
                            id: 'short-term',
                            title: t('riskScore.roadmap.sections.shortTerm.title'),
                            timeframe: t('riskScore.roadmap.sections.shortTerm.timeframe'),
                            color: 'orange',
                            count: 3,
                            icon: 'clock',
                            progress: 1,
                            expanded: false
                          },
                          {
                            id: 'long-term',
                            title: t('riskScore.roadmap.sections.longTerm.title'),
                            timeframe: t('riskScore.roadmap.sections.longTerm.timeframe'),
                            color: 'green',
                            count: 3,
                            icon: 'check-circle',
                            status: 'up-to-date',
                            expanded: false
                          }
                        ].map((section, index) => {
                          const sectionTone: Tone = section.color === 'red' ? 'danger' : section.color === 'orange' ? 'warn' : 'mint';

                          return (
                            <div
                              key={section.id}
                              className={`timeline-item-enhanced rsg-tl-item rsg-t-${sectionTone}${section.expanded ? ' is-open' : ''}`}
                              data-section={section.id}
                            >
                              <div className="timeline-marker rsg-tl-marker">
                                {/* Marker */}
                                <span className={`rsg-tl-dot${section.color === 'red' ? ' rsg-pulse' : ''}`}>
                                  {section.icon === 'alert' && <CircleAlert size={13} strokeWidth={2.2} />}
                                  {section.icon === 'clock' && <Clock size={13} strokeWidth={2.2} />}
                                  {section.icon === 'check-circle' && <CircleCheck size={13} strokeWidth={2.2} />}
                                  {index < 2 && <span className="rsg-tl-dot-sub" />}
                                </span>

                                <div className="rsg-tl-body">
                                  {/* Header */}
                                  <button
                                    type="button"
                                    className="timeline-header rsg-tl-header"
                                    onClick={(e) => {
                                      const target = e.currentTarget.closest('.timeline-item-enhanced') as HTMLElement;
                                      if (target) {
                                        target.classList.toggle('expanded');
                                        // Toggle all other sections
                                        document.querySelectorAll('.timeline-item-enhanced').forEach(item => {
                                          if (item !== target) {
                                            item.classList.remove('expanded');
                                          }
                                        });
                                      }
                                    }}
                                  >
                                    <span className="rsg-tl-heading">
                                      <span className="rsg-inline">
                                        <span className="rsg-tl-title">{section.title}</span>
                                        <span className={chip(sectionTone)}>{section.timeframe}</span>
                                      </span>
                                      <span className="bd-meta">
                                        {section.id === 'immediate' && t('riskScore.roadmap.sections.immediate.description')}
                                        {section.id === 'short-term' && t('riskScore.roadmap.sections.shortTerm.description')}
                                        {section.id === 'long-term' && t('riskScore.roadmap.sections.longTerm.description')}
                                      </span>
                                    </span>

                                    <span className="rsg-inline end">
                                      {section.id === 'immediate' && section.count > 0 && (
                                        <span className={chip('danger')}>
                                          <span className="dot rsg-ping" />
                                          <Trans t={t} i18nKey="riskScore.roadmap.criticalChip" count={section.count} values={{ count: section.count }} components={mono} />
                                        </span>
                                      )}

                                      {section.id === 'short-term' && (
                                        <span className="rsg-inline">
                                          <span className="bd-progress warn rsg-mini-progress"><i style={{ width: '33.333%' }} /></span>
                                          <span className="bd-meta bd-mono">{section.progress}/3</span>
                                        </span>
                                      )}

                                      {section.id === 'long-term' && (
                                        <span className="rsg-inline rsg-c-mint rsg-strong-sm">
                                          <CircleCheck size={15} strokeWidth={2} />
                                          {t('riskScore.roadmap.upToDate')}
                                        </span>
                                      )}

                                      <ChevronDown
                                        size={18}
                                        strokeWidth={2}
                                        className={`rsg-chevron${section.expanded ? ' is-flipped' : ''}`}
                                      />
                                    </span>
                                  </button>

                                  {/* Expandable Content */}
                                  <div className={`timeline-content-enhanced rsg-tl-content${section.expanded ? ' is-open' : ''}`}>
                                    {section.id === 'immediate' && (
                                      <div className="rsg-stack tight">
                                        <ul className="rsg-tl-list">
                                          {section.count > 0 ? (
                                            <>
                                              <li className="rsg-tl-row rsg-t-danger">
                                                <div className="rsg-tl-row-id">
                                                  <span className="rsg-dot rsg-ping-dot" />
                                                  <div className="rsg-id-text">
                                                    <span className="rsg-strong-sm">{t('riskScore.roadmap.immediate.patch', { count: section.count })}</span>
                                                    <span className="bd-meta">{t('riskScore.roadmap.immediate.patchSub')}</span>
                                                  </div>
                                                </div>
                                                <div className="rsg-actions">
                                                  <span className={chip('danger')}>{t('riskScore.riskLabels.high')}</span>
                                                  <button
                                                    type="button"
                                                    onClick={() => handleQuickFix('critical')}
                                                    className="bd-btn danger-soft rsg-btn-sm"
                                                  >
                                                    {t('riskScore.roadmap.immediate.fixNow')}
                                                    <ArrowRight size={15} strokeWidth={2} />
                                                  </button>
                                                </div>
                                              </li>

                                              {deviceData.riskScore?.cvePercentage && deviceData.riskScore.cvePercentage > 70 ? (
                                                <li className="rsg-tl-row rsg-t-warn">
                                                  <div className="rsg-tl-row-id">
                                                    <span className="rsg-dot" />
                                                    <div className="rsg-id-text">
                                                      <span className="rsg-strong-sm">{t('riskScore.roadmap.immediate.reviewPerms')}</span>
                                                      <span className="bd-meta">{t('riskScore.roadmap.immediate.reviewPermsSub')}</span>
                                                    </div>
                                                  </div>
                                                  <div className="rsg-actions">
                                                    <div className="rsg-id-text">
                                                      <span className="bd-meta">{t('riskScore.common.riskLevel')}</span>
                                                      <div className="bd-progress warn rsg-risk-progress">
                                                        <i style={{ width: `${deviceData.riskScore.cvePercentage}%` }} />
                                                      </div>
                                                    </div>
                                                    <button
                                                      type="button"
                                                      onClick={() => handleQuickFix('app')}
                                                      className="bd-btn ghost rsg-btn-sm"
                                                    >
                                                      {t('riskScore.common.review')}
                                                    </button>
                                                  </div>
                                                </li>
                                              ) : null}
                                            </>
                                          ) : (
                                            <li className="rsg-tl-row rsg-t-mint">
                                              <div className="rsg-tl-row-id">
                                                <span className="rsg-dot" />
                                                <div className="rsg-id-text">
                                                  <span className="rsg-strong-sm rsg-c-mint">{t('riskScore.roadmap.immediate.noCritical')}</span>
                                                  <span className="bd-meta">{t('riskScore.roadmap.immediate.secure')}</span>
                                                </div>
                                              </div>
                                            </li>
                                          )}

                                          <li className="rsg-tl-row rsg-t-info">
                                            <div className="rsg-tl-row-id">
                                              <span className="rsg-dot" />
                                              <div className="rsg-id-text">
                                                <span className="rsg-strong-sm">{t('riskScore.roadmap.immediate.autoUpdates')}</span>
                                                <span className="bd-meta">{t('riskScore.roadmap.immediate.autoUpdatesSub')}</span>
                                              </div>
                                            </div>
                                            <div className="rsg-actions">
                                              <span className="rsg-inline rsg-c-info rsg-strong-sm">
                                                <Check size={15} strokeWidth={2} />
                                                {t('riskScore.common.recommended')}
                                              </span>
                                              <button type="button" className="bd-btn secondary rsg-btn-sm">
                                                {t('riskScore.common.configure')}
                                              </button>
                                            </div>
                                          </li>
                                        </ul>

                                        <div className="rsg-tl-row rsg-t-neutral rsg-eta">
                                          <div className="rsg-tl-row-id">
                                            <span className="rsg-ico"><Clock size={18} strokeWidth={1.9} /></span>
                                            <div className="rsg-id-text">
                                              <span className="bd-meta">{t('riskScore.roadmap.immediate.eta')}</span>
                                              <span className="bd-mono rsg-eta-val">
                                                {t('riskScore.common.minutes', { count: section.count > 0 ? 45 : 15 })}
                                              </span>
                                            </div>
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() => handleQuickFix('all')}
                                            className="bd-btn primary"
                                          >
                                            {t('riskScore.roadmap.immediate.start')}
                                            <ArrowRight size={16} strokeWidth={2} />
                                          </button>
                                        </div>
                                      </div>
                                    )}

                                    {section.id === 'short-term' && (
                                      <div className="rsg-stack tight">
                                        <ul className="rsg-tl-list">
                                          <li className="rsg-tl-row rsg-t-mint">
                                            <div className="rsg-tl-row-id">
                                              <span className="rsg-dot" />
                                              <div className="rsg-id-text">
                                                <span className="rsg-strong-sm">{t('riskScore.roadmap.shortTerm.completeRecs')}</span>
                                                <span className="bd-meta">{t('riskScore.roadmap.shortTerm.completeRecsSub')}</span>
                                              </div>
                                            </div>
                                            <button type="button" className="bd-btn secondary rsg-btn-sm">{t('riskScore.common.review')}</button>
                                          </li>
                                          <li className="rsg-tl-row rsg-t-violet">
                                            <div className="rsg-tl-row-id">
                                              <span className="rsg-dot" />
                                              <div className="rsg-id-text">
                                                <span className="rsg-strong-sm">{t('riskScore.roadmap.shortTerm.malwareScan')}</span>
                                                <span className="bd-meta">{t('riskScore.roadmap.shortTerm.malwareScanSub')}</span>
                                              </div>
                                            </div>
                                            <button type="button" className="bd-btn secondary rsg-btn-sm">{t('riskScore.roadmap.shortTerm.startScan')}</button>
                                          </li>
                                          <li className="rsg-tl-row rsg-t-warn">
                                            <div className="rsg-tl-row-id">
                                              <span className="rsg-dot" />
                                              <div className="rsg-id-text">
                                                <span className="rsg-strong-sm">{t('riskScore.roadmap.shortTerm.blockedComms')}</span>
                                                <span className="bd-meta">{t('riskScore.roadmap.shortTerm.blockedCommsSub')}</span>
                                              </div>
                                            </div>
                                            <button type="button" className="bd-btn secondary rsg-btn-sm">{t('riskScore.roadmap.shortTerm.checkLogs')}</button>
                                          </li>
                                        </ul>

                                        <div className="rsg-callout rsg-t-warn rsg-stack tight">
                                          <div className="rsg-split">
                                            <span className="rsg-strong-sm">{t('riskScore.roadmap.shortTerm.weeklyProgress')}</span>
                                            <span className="bd-mono rsg-strong rsg-tone-text">{t('riskScore.roadmap.shortTerm.completed', { done: section.progress, total: 3 })}</span>
                                          </div>
                                          <div className="bd-progress warn">
                                            <i style={{ width: '33%' }} />
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    {section.id === 'long-term' && (
                                      <div className="rsg-stack tight">
                                        <ul className="rsg-tl-list">
                                          <li className="rsg-tl-row rsg-t-neutral">
                                            <div className="rsg-tl-row-id">
                                              <span className="rsg-dot" />
                                              <div className="rsg-id-text">
                                                <span className="rsg-strong-sm">{t('riskScore.roadmap.longTerm.audits')}</span>
                                                <span className="bd-meta">{t('riskScore.roadmap.longTerm.auditsSub')}</span>
                                              </div>
                                            </div>
                                            <button type="button" className="bd-btn ghost rsg-btn-sm">{t('riskScore.roadmap.longTerm.schedule')}</button>
                                          </li>
                                          <li className="rsg-tl-row rsg-t-info">
                                            <div className="rsg-tl-row-id">
                                              <span className="rsg-dot" />
                                              <div className="rsg-id-text">
                                                <span className="rsg-strong-sm">{t('riskScore.roadmap.longTerm.updateApps')}</span>
                                                <span className="bd-meta">{t('riskScore.roadmap.longTerm.updateAppsSub')}</span>
                                              </div>
                                            </div>
                                            <button type="button" className="bd-btn secondary rsg-btn-sm">{t('riskScore.roadmap.longTerm.checkUpdates')}</button>
                                          </li>
                                          <li className="rsg-tl-row rsg-t-info">
                                            <div className="rsg-tl-row-id">
                                              <span className="rsg-dot" />
                                              <div className="rsg-id-text">
                                                <span className="rsg-strong-sm">{t('riskScore.roadmap.longTerm.reviewSettings')}</span>
                                                <span className="bd-meta">{t('riskScore.roadmap.longTerm.reviewSettingsSub')}</span>
                                              </div>
                                            </div>
                                            <button type="button" className="bd-btn secondary rsg-btn-sm">{t('riskScore.roadmap.longTerm.optimize')}</button>
                                          </li>
                                        </ul>

                                        <Callout
                                          tone="mint"
                                          icon={<CircleCheck size={18} strokeWidth={1.9} />}
                                          title={t('riskScore.roadmap.longTerm.calloutTitle')}
                                        >
                                          {t('riskScore.roadmap.longTerm.calloutText')}
                                        </Callout>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Action Cards */}
                      <div className="rsg-action-grid">
                        <div className="rsg-action rsg-t-info">
                          <div className="rsg-inline">
                            <span className="rsg-ico sm"><WandSparkles size={16} strokeWidth={1.9} /></span>
                            <span className="rsg-strong">{t('riskScore.roadmap.actions.autoFixTitle')}</span>
                          </div>
                          <p className="bd-meta rsg-flush">{t('riskScore.roadmap.actions.autoFixText')}</p>
                          <button
                            type="button"
                            onClick={() => handleQuickFix('auto')}
                            className="bd-btn primary rsg-btn-sm rsg-full"
                          >
                            {t('riskScore.roadmap.actions.autoFixButton')}
                          </button>
                        </div>

                        <div className="rsg-action rsg-t-mint">
                          <div className="rsg-inline">
                            <span className="rsg-ico sm"><CalendarClock size={16} strokeWidth={1.9} /></span>
                            <span className="rsg-strong">{t('riskScore.roadmap.actions.scheduleTitle')}</span>
                          </div>
                          <p className="bd-meta rsg-flush">{t('riskScore.roadmap.actions.scheduleText')}</p>
                          <button type="button" className="bd-btn secondary rsg-btn-sm rsg-full">
                            {t('riskScore.roadmap.actions.scheduleButton')}
                          </button>
                        </div>

                        <div className="rsg-action rsg-t-violet">
                          <div className="rsg-inline">
                            <span className="rsg-ico sm"><Download size={16} strokeWidth={1.9} /></span>
                            <span className="rsg-strong">{t('riskScore.roadmap.actions.reportTitle')}</span>
                          </div>
                          <p className="bd-meta rsg-flush">{t('riskScore.roadmap.actions.reportText')}</p>
                          <button type="button" className="bd-btn secondary rsg-btn-sm rsg-full">
                            {t('riskScore.roadmap.actions.reportButton')}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Implementation Progress */}
                    <div className="rsg-nested rsg-impl">
                      <h5 className="rsg-block-title flush"><ChartColumn size={16} strokeWidth={1.9} />{t('riskScore.impl.title')}</h5>

                      <div className="rsg-stack tight">
                        <div className="rsg-split">
                          <span className="rsg-strong-sm">{t('riskScore.impl.overall')}</span>
                          <span className="bd-mono rsg-strong rsg-c-info">
                            {Math.round((remediationActions.filter(action => action.completed).length / Math.max(remediationActions.length, 1)) * 100)}%
                          </span>
                        </div>
                        <div className="bd-progress rsg-progress-md">
                          <i
                            style={{
                              width: `${Math.round((remediationActions.filter(action => action.completed).length / Math.max(remediationActions.length, 1)) * 100)}%`,
                              background: 'var(--info)'
                            }}
                          />
                        </div>
                        <div className="rsg-split bd-meta">
                          <span>{t('riskScore.impl.pending')}</span>
                          <span>{t('riskScore.impl.inProgress')}</span>
                          <span>{t('riskScore.impl.completed')}</span>
                        </div>
                      </div>

                      <div className="rsg-stack tight">
                        <div className="rsg-split">
                          <span className="rsg-strong-sm">{t('riskScore.impl.riskReduction')}</span>
                          <span className="bd-mono rsg-strong rsg-c-mint">
                            {Math.min(Math.round((100 - healthScore) / 20 * 100), 100)}%
                          </span>
                        </div>
                        <div className="bd-progress rsg-progress-md">
                          <i style={{ width: `${Math.min(Math.round((100 - healthScore) / 20 * 100), 100)}%` }} />
                        </div>
                        <span className="bd-meta">
                          <Trans t={t} i18nKey="riskScore.impl.target" values={{ current: healthScore, target: Math.max(healthScore - 20, 80) }} components={mono} />
                        </span>
                      </div>

                      {/* Issues Summary */}
                      <div className="rsg-grid-2 tight fixed">
                        <div className="rsg-callout rsg-t-danger rsg-tile">
                          <span className="bd-stat-num rsg-num-md rsg-tone-text">{totalIssues}</span>
                          <span className="bd-meta">{t('riskScore.common.activeIssues')}</span>
                          <button
                            type="button"
                            onClick={() => handleQuickFix('all')}
                            className="rsg-mini-btn rsg-full"
                          >
                            {t('riskScore.common.fixAllIssues')}
                          </button>
                        </div>
                        <div className="rsg-callout rsg-t-mint rsg-tile">
                          <span className="bd-stat-num rsg-num-md rsg-tone-text">
                            {remediationActions.filter(a => a.completed).length}
                          </span>
                          <span className="bd-meta">{t('riskScore.impl.resolvedIssues')}</span>
                        </div>
                      </div>

                      <button type="button" className="bd-btn primary rsg-full" onClick={handleFixAll}>
                        <Check size={18} strokeWidth={2} />
                        {t('riskScore.impl.autoImplementAll')}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Footer Actions */}
                <div className="rsg-card-ft">
                  <span className="bd-meta rsg-inline">
                    <CalendarClock size={14} strokeWidth={1.9} />
                    <Trans
                      t={t}
                      i18nKey="riskScore.footer.lastUpdated"
                      values={{
                        date: new Date().toLocaleString(locale, {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }),
                      }}
                      components={mono}
                    />
                  </span>
                  <div className="rsg-actions">
                    <button type="button" className="bd-btn secondary">
                      <FileText size={16} strokeWidth={1.9} />
                      {t('riskScore.footer.generateReport')}
                    </button>
                    <button type="button" className="bd-btn primary" onClick={handleFixAll}>
                      <Check size={16} strokeWidth={2} />
                      {t('riskScore.footer.implementAll')}
                    </button>
                  </div>
                </div>
              </section>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RiskScoreCardGrid;
